/**
 * EmailComposerV2 — Lean, focused redesign laut Pflichtenheft (PDF Emergent_Email_Composer_Redesign).
 *
 * Highlights:
 *  - Schlankes Modal mit Titelleiste „Neue E-Mail" + Min/Max/Close
 *  - Empfänger als entfernbare Chip-Pills inkl. Adressbuch-Picker
 *  - CC/BCC einklappbar in der „Von"-Zeile
 *  - Collapsible Toolbar (Default sichtbar, localStorage-State)
 *  - TipTap WYSIWYG mit Toggle Quelltext/Vorschau
 *  - Footer-Icons: KI · Anhänge · Vorlagen · Signatur · Mehr · Senden · Papierkorb
 *  - Vorlagen-Modal mit Suche & Branchen-Icons
 *
 * Reuses existing services: templateService, emailService, outlookService.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Mail, Send, X, Paperclip, ChevronDown, ChevronUp, Loader2, Eye, Edit, Code2,
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  Heading1, Heading2, AlignLeft, AlignCenter, AlignRight,
  Sparkles, FileText, Edit3, MoreHorizontal, Trash2, Search, UserPlus, Filter,
  Minus, Square, ChevronLeft, ChevronRight, AlertTriangle, Clock, Mail as MailIcon, Bell, Lock,
  HardDrive, Check, Blocks, Hash, Settings, Database, FolderKanban, FolderPlus, BookOpen, ClipboardPaste, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapLink from '@tiptap/extension-link';
import TipTapImage from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import TipTapUnderline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import FontSize from '../tiptap/FontSizeExtension';
import { MAIL_SCHRIFTEN, MAIL_SCHRIFTGROESSEN } from '../tiptap/mailFonts';
import DraggableResizableWindow from '../ui/DraggableResizableWindow';

import templateService from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantIndustries } from '../../hooks/useTenantIndustries';
import { ermittleProduktlinien, passtZuMandant, produktlinieLabel } from '../../utils/produktlinien';
import WhatsAppBildWindow from '../whatsapp/WhatsAppBildWindow';
import emailService from '../../services/emailService';
import outlookService from '../../services/outlookService';
import api from '../../services/api';
import settingsService from '../../services/settingsService';
import VorlagenPillen, { FavoritStern, favoritenZuerst } from '../templates/VorlagenPillen';
import apiFetch from '../../utils/apiFetch';
import EmailRecipientPicker from './EmailRecipientPicker';
import EmailDocumentPicker from './EmailDocumentPicker';
import useDraftPersistence from '../../hooks/useDraftPersistence';
import { useContext } from 'react';
import TabContext from '../../contexts/TabContext';
import {
  BILD_RESIZE, bildDateienAus, bildVorbereiten, bildHtml, fuegeBildInView,
  htmlMitText, htmlMitEingebettetenBildern, hinweisFehlendeBilder,
} from './editorBilder';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const LS_TOOLBAR_KEY = 'emailComposerToolbarOpen';
const TRANS = 'transition-all duration-150 ease-out';

const splitEmails = (raw) =>
  (raw || '')
    .split(/[,;\n\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─────────────────────────────────────────────────────────────────────────────
// RecipientChips — entfernbare Pill-Tags + Freitext + Adressbuch
// ─────────────────────────────────────────────────────────────────────────────
function RecipientChips({ emails, onChange, placeholder, onOpenAddressbook, onOpenDatenfilter, testIdPrefix }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  // Vorschläge aus dem CRM (Adressen + Ansprechpartner) beim Tippen von Name oder E-Mail
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    const q = draft.trim();
    if (q.length < 2 || /[,;]/.test(q)) { setSuggestions([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/addresses/email-suggest', { params: { q, limit: 8 } });
        if (cancelled) return;
        const list = (res.data?.suggestions || []).filter((s) => !emails.includes(s.email));
        setSuggestions(list);
        setHighlight(0);
        setSuggestOpen(true);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const addEmails = (raw) => {
    const fresh = splitEmails(raw).filter((e) => !emails.includes(e));
    if (fresh.length) onChange([...emails, ...fresh]);
  };

  const pickSuggestion = (s) => {
    if (s?.email && !emails.includes(s.email)) onChange([...emails, s.email]);
    setDraft('');
    setSuggestions([]);
    setSuggestOpen(false);
    inputRef.current?.focus();
  };

  const removeAt = (idx) => onChange(emails.filter((_, i) => i !== idx));

  const showSuggestions = suggestOpen && suggestions.length > 0 && draft.trim().length >= 2;

  const onKeyDown = (e) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Escape') { e.preventDefault(); setSuggestOpen(false); return; }
      // Enter/Tab übernimmt den markierten Vorschlag, sofern kein vollständiges Mail-Format getippt wurde
      if ((e.key === 'Enter' || e.key === 'Tab') && !isValidEmail(draft.trim())) {
        e.preventDefault();
        pickSuggestion(suggestions[highlight]);
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (draft.trim()) {
        e.preventDefault();
        addEmails(draft);
        setDraft('');
      }
    } else if (e.key === 'Backspace' && !draft && emails.length > 0) {
      removeAt(emails.length - 1);
    }
  };

  const onBlur = () => {
    setSuggestOpen(false);
    // Nur echte E-Mail-Adressen beim Verlassen übernehmen; ein angefangener Name bleibt stehen
    if (draft.trim() && splitEmails(draft).every(isValidEmail)) {
      addEmails(draft);
      setDraft('');
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text && /[,;\n\s]/.test(text)) {
      e.preventDefault();
      addEmails(text);
      setDraft('');
    }
  };

  return (
    <div
      className="relative flex flex-wrap items-center gap-1.5 flex-1 min-w-0 py-1.5 cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {emails.map((em, idx) => {
        const valid = isValidEmail(em);
        return (
          <span
            key={`${em}-${idx}`}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border ${
              valid
                ? 'bg-blue-50 text-blue-800 border-blue-200'
                : 'bg-amber-50 text-amber-800 border-amber-300'
            }`}
            data-testid={`${testIdPrefix}-chip-${idx}`}
            title={valid ? em : `${em} (ungültige Adresse)`}
          >
            {!valid && <AlertTriangle className="w-3 h-3" />}
            <span className="truncate max-w-[260px]">{em}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
              className="hover:bg-blue-100 rounded p-0.5"
              aria-label="Empfänger entfernen"
              data-testid={`${testIdPrefix}-chip-remove-${idx}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      <input
        ref={inputRef}
        type="text"
        inputMode="email"
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setSuggestOpen(true); }}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={() => setSuggestOpen(true)}
        onPaste={onPaste}
        placeholder={emails.length === 0 ? placeholder : ''}
        autoComplete="off"
        className="flex-1 min-w-[140px] py-0.5 text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
        data-testid={`${testIdPrefix}-input`}
      />
      {showSuggestions && (
        <ul
          className="absolute left-0 top-full z-50 mt-1 w-full max-w-md max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
          role="listbox"
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.email}-${i}`}
              role="option"
              aria-selected={i === highlight}
              // mousedown statt click: verhindert, dass der Blur des Eingabefelds vorher feuert
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === highlight ? 'bg-blue-50' : ''}`}
              data-testid={`${testIdPrefix}-suggestion-${i}`}
            >
              <div className="text-sm text-gray-900 truncate">{s.name || s.email}</div>
              <div className="text-xs text-gray-500 truncate">{s.email}{s.subline ? ` · ${s.subline}` : ''}</div>
            </li>
          ))}
        </ul>
      )}
      {onOpenAddressbook && (
        <button
          type="button"
          onClick={onOpenAddressbook}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600"
          title="Adressbuch öffnen"
          data-testid={`${testIdPrefix}-addressbook-btn`}
        >
          <UserPlus className="w-4 h-4" />
        </button>
      )}
      {onOpenDatenfilter && (
        <button
          type="button"
          onClick={onOpenDatenfilter}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600"
          title="Empfänger aus Datenfilter (1 im „An“, Rest im BCC)"
          data-testid={`${testIdPrefix}-datenfilter-btn`}
        >
          <Filter className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TipTap-Editor Wrapper mit Collapsible Toolbar + Vorschau/Quelltext-Toggle
// ─────────────────────────────────────────────────────────────────────────────
// Komplexe HTML-Templates (Newsletter, Angebots-Layouts mit Tabellen, Inline-Styles,
// Base64-Bildern) werden von TipTap im WYSIWYG-Modus zwangsläufig "vereinfacht"
// (Tabellen, <style>-Blöcke, <font>, <center> sind nicht im StarterKit-Schema).
// → Wir erkennen solche Templates beim Laden und schalten automatisch in den
// Vorschau-Modus, der das HTML 1:1 per dangerouslySetInnerHTML rendert.

const _isComplexHtml = (html) => {
  if (!html) return false;
  const h = html.toLowerCase();
  return (
    h.includes('<table') ||
    h.includes('<style') ||
    h.includes('<center') ||
    h.includes('<font') ||
    h.includes('data:image/') ||
    /style="[^"]*(?:background|float|grid|flex|width|height)/.test(h)
  );
};

function ComposerEditor({ value, onChange, onEditorReady, modeRef, pristineHtml, onPristineChange, onUnlockPristine, textBlocksRef, resolveSnippetRef, onBodyContextMenu, bodyApiRef }) {
  const [toolbarOpen, setToolbarOpen] = useState(() => {
    try {
      return localStorage.getItem(LS_TOOLBAR_KEY) !== '0';
    } catch { return true; }
  });
  // Hält die Editor-Instanz für den TB-Tastatur-Handler (Closure-sicher via Ref)
  const editorInstanceRef = useRef(null);

  // Slash-Command-artige Textbaustein-Vorschlagsliste (Trigger: "TB" im Editor)
  const [tbSuggest, setTbSuggest] = useState({ open: false, items: [], digits: '', top: 0, left: 0, from: 0, to: 0, index: 0 });
  const tbSuggestRef = useRef(tbSuggest);
  useEffect(() => { tbSuggestRef.current = tbSuggest; }, [tbSuggest]);

  const closeTbSuggest = useCallback(() => {
    setTbSuggest((s) => (s.open ? { ...s, open: false } : s));
  }, []);

  const updateTbSuggest = useCallback(() => {
    const ed = editorInstanceRef.current;
    if (!ed || !ed.isFocused) { closeTbSuggest(); return; }
    const sel = ed.view.state.selection;
    if (!sel.empty) { closeTbSuggest(); return; }
    const $from = sel.$from;
    const textBefore = $from.parent.textBetween(0, $from.parentOffset, '\n', '\n');
    const m = /(?:^|[\s(>\u00a0])(TB)(\d*)$/i.exec(textBefore);
    if (!m) { closeTbSuggest(); return; }
    const digits = m[2];
    const blocks = (textBlocksRef && textBlocksRef.current) || [];
    const items = blocks.filter((b) => !digits || String(b.tb_number).startsWith(digits));
    if (items.length === 0) { closeTbSuggest(); return; }
    const tokenLen = m[1].length + m[2].length;
    const to = sel.from;
    const from = to - tokenLen;
    let coords;
    try { coords = ed.view.coordsAtPos(to); } catch { closeTbSuggest(); return; }
    setTbSuggest((s) => ({
      open: true, items, digits,
      top: coords.bottom + 4, left: coords.left, from, to,
      index: s.open ? Math.min(s.index, items.length - 1) : 0,
    }));
  }, [closeTbSuggest, textBlocksRef]);

  const insertTbSuggest = useCallback(async (block) => {
    const ed = editorInstanceRef.current;
    const { from, to } = tbSuggestRef.current;
    closeTbSuggest();
    if (!ed || !block) return;
    ed.chain().focus().deleteRange({ from, to }).run();
    let html = block.body_html || '';
    if (resolveSnippetRef && resolveSnippetRef.current && html.includes('{{')) {
      try { html = await resolveSnippetRef.current(html); } catch { /* Rohtext behalten */ }
    }
    editorInstanceRef.current?.chain().focus().insertContent(html).run();
  }, [closeTbSuggest, resolveSnippetRef]);
  // wysiwyg | source | preview.
  // Bei komplexen HTML-Templates (pristineHtml gesetzt) wird das Layout im IFrame
  // gerendert. "Bearbeiten" macht dieses IFrame direkt editierbar (designMode) —
  // Tabellen, Inline-Styles & Bilder bleiben dabei 1:1 erhalten (WYSIWYG wie Strato).
  const [mode, setMode] = useState('wysiwyg');
  // Steuert, ob das Vorschau-IFrame editierbar ist (In-Place-WYSIWYG für komplexes HTML)
  const [iframeEditable, setIframeEditable] = useState(false);
  const iframeRef = useRef(null);
  const isLocked = !!pristineHtml;
  // True, wenn wir das komplexe HTML direkt im IFrame bearbeiten
  const richEdit = isLocked && mode === 'preview' && iframeEditable;
  // Reine Vorschau / Quelltext: Schriftauswahl hätte keine Wirkung → ausgrauen
  const fontSelectDisabled = !richEdit && mode !== 'wysiwyg';

  // Externer Zugriff auf setMode (z.B. nach Template-Load → 'preview' erzwingen)
  useEffect(() => {
    if (modeRef) modeRef.current = { setMode, getMode: () => mode };
  }, [mode, modeRef]);

  // Wenn ein komplexes HTML-Template extern gesetzt wird (pristineHtml), automatisch
  // in den IFrame-Vorschaumodus wechseln — damit Inline-Styles/Layout sichtbar sind
  // (z.B. „Erstkontakt-Mail entwerfen" aus der Website-Analyse).
  // Direkt bearbeitbar (In-Place-WYSIWYG), damit Tippen und Einfügen per Strg+V /
  // Rechtsklick sofort funktionieren; reine Vorschau weiterhin über „Vorschau“.
  useEffect(() => {
    if (isLocked) { setMode('preview'); setIframeEditable(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked]);
  const editor = useEditor({
    extensions: [
      // TipTap v3: StarterKit enthaelt link & underline bereits → deaktivieren,
      // da wir sie unten manuell mit eigener Konfiguration registrieren.
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      TipTapUnderline,
      TipTapLink.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-600 underline' } }),
      // Bilder: base64 erlaubt (eingefügte/hochgeladene Bilder; beim Senden als CID-Anhang),
      // Ziehpunkte an den vier Ecken zum Skalieren – Seitenverhältnis bleibt erhalten.
      TipTapImage.configure({
        inline: false,
        allowBase64: true,
        resize: BILD_RESIZE,
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // Schriftart/-größe (wie im Notizbuch) – als Inline-Style, das zeigen alle Mailprogramme an
      TextStyle,
      FontFamily.configure({ types: ['textStyle'] }),
      FontSize,
      Placeholder.configure({ placeholder: 'Schreib deine Nachricht …' }),
    ],
    content: value || '',
    // Textbaustein-Vorschlagsliste (Slash-Command-Stil): "TB"+Ziffern blendet
    // eine gefilterte Liste ein. ↑/↓ navigieren, Enter/Tab/Klick fügt ein,
    // "TB"+Nr+Leertaste fügt bei exakter Nummer sofort ein.
    editorProps: {
      // Bilder per Strg+V / Einfügen (z. B. Screenshot) und per Drag & Drop einfügen
      handlePaste: (view, event) => {
        const cd = event.clipboardData;
        const bilder = bildDateienAus(cd);
        const html = cd?.getData('text/html') || '';
        // Word/Outlook: Text + Bilder → beides einfügen, Bilder an ihrer Stelle im Text
        if ((bilder.length || /<img[^>]+src=["']?file:/i.test(html)) && htmlMitText(html) && view.pasteHTML) {
          event.preventDefault();
          htmlMitEingebettetenBildern(html, bilder).then(({ html: fertig, fehlend }) => {
            if (!view.isDestroyed) view.pasteHTML(fertig);
            hinweisFehlendeBilder(fehlend);
          });
          return true;
        }
        if (!bilder.length) return false;
        event.preventDefault();
        bilder.forEach((f) => fuegeBildInView(view, f));
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const bilder = bildDateienAus(event.dataTransfer);
        if (!bilder.length) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        bilder.forEach((f) => fuegeBildInView(view, f, pos));
        return true;
      },
      handleKeyDown: (view, event) => {
        const sug = tbSuggestRef.current;
        if (sug.open && sug.items.length) {
          if (event.key === 'ArrowDown') {
            setTbSuggest((s) => ({ ...s, index: (s.index + 1) % s.items.length }));
            event.preventDefault(); return true;
          }
          if (event.key === 'ArrowUp') {
            setTbSuggest((s) => ({ ...s, index: (s.index - 1 + s.items.length) % s.items.length }));
            event.preventDefault(); return true;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            insertTbSuggest(sug.items[sug.index]);
            event.preventDefault(); return true;
          }
          if (event.key === 'Escape') { closeTbSuggest(); event.preventDefault(); return true; }
          if (event.key === ' ') {
            const exact = sug.items.find((b) => String(b.tb_number) === sug.digits);
            if (exact) { insertTbSuggest(exact); event.preventDefault(); return true; }
          }
        }
        return false;
      },
    },
    // WICHTIG: TipTap darf den State NICHT überschreiben, solange ein komplexes
    // HTML-Template gesperrt ist (sonst werden Tabellen/Inline-Styles zerstört).
    onUpdate: ({ editor: ed }) => {
      if (!isLocked) onChange(ed.getHTML());
      updateTbSuggest();
    },
    onSelectionUpdate: () => { updateTbSuggest(); },
    onBlur: () => { setTimeout(closeTbSuggest, 150); },
  });

  // Editor-Instanz für den TB-Tastatur-Handler bereitstellen
  useEffect(() => {
    editorInstanceRef.current = editor;
  }, [editor]);

  // Sync external value-changes (z.B. nach Template-Load) — aber NUR wenn nicht
  // gesperrt (sonst würde TipTap das Template-HTML beim Schreiben strippen).
  useEffect(() => {
    if (!editor) return;
    if (isLocked) return; // pristineHtml ist in iframe sichtbar
    if (value === editor.getHTML()) return;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      try {
        editor.commands.setContent(value || '', false, { preserveWhitespace: 'full' });
      } catch {
        editor.commands.setContent(value || '', false);
      }
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(apply, { timeout: 200 });
      return () => { cancelled = true; window.cancelIdleCallback?.(id); };
    }
    const tid = setTimeout(apply, 0);
    return () => { cancelled = true; clearTimeout(tid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLocked]);

  // Render pristineHtml im sandboxed IFrame — isoliert von Parent-CSS,
  // Tabellen/Inline-Styles/<style>-Blöcke werden 1:1 angezeigt.
  // Content-Sync: srcdoc wird NUR bei externen Änderungen (Template-Load) neu gesetzt,
  // nicht bei eigener Tasteneingabe (Guard über iframe._lastSrc) → Cursor bleibt erhalten.
  useEffect(() => {
    const html = pristineHtml || value || '';
    const iframe = iframeRef.current;
    if (mode !== 'preview' || !iframe) return;
    if (iframe._lastSrc !== html) {
      iframe._lastSrc = html;
      try { iframe.srcdoc = html; } catch { /* ignore */ }
    }
  }, [mode, pristineHtml, value]);

  // Setup: Editierbarkeit (designMode) + Auto-Resize. Läuft nur, wenn sich Modus oder
  // Editier-Status ändert; onLoad räumt vorherige Listener/Observer auf (keine Duplikate).
  useEffect(() => {
    if (mode !== 'preview') return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onLoad = () => {
      try {
        try { iframe._syncDispose?.(); } catch { /* noop */ }
        try { iframe._roDispose?.(); } catch { /* noop */ }
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        // designMode='on' macht das gesamte Dokument editierbar, OHNE ein
        // contenteditable-Attribut in den versendeten HTML-Code zu schreiben.
        try { doc.designMode = iframeEditable ? 'on' : 'off'; } catch { /* ignore */ }
        if (iframeEditable) {
          const sync = () => {
            try {
              let full = doc.documentElement ? doc.documentElement.outerHTML : doc.body.innerHTML;
              // Editor-Artefakte entfernen (falls der Browser welche hinterlässt)
              full = full.replace(/\s*contenteditable="(?:true|false|inherit)"/gi, '');
              iframe._lastSrc = full; // verhindert Reload durch den eigenen Update
              onPristineChange?.(full);
            } catch { /* ignore */ }
          };
          doc.addEventListener('input', sync);
          doc.addEventListener('blur', sync, true);
          // Manueller Paste-Handler: In einem sandboxed IFrame (designMode) wird das
          // native Einfügen via STRG+V teilweise blockiert. Wir fangen das paste-Event
          // ab und fügen den Inhalt selbst per execCommand ein (Parent-Kontext, same-origin).
          const onPasteIframe = (e) => {
            try {
              const cd = e.clipboardData || iframe.contentWindow?.clipboardData;
              if (!cd) return; // kein Zugriff → natives Verhalten zulassen
              // Bild aus der Zwischenablage (Screenshot, kopiertes Bild)
              const bilder = bildDateienAus(cd);
              const htmlCb = cd.getData('text/html');
              // Word/Outlook: Text + Bilder zusammen einfügen
              if ((bilder.length || /<img[^>]+src=["']?file:/i.test(htmlCb)) && htmlMitText(htmlCb)) {
                e.preventDefault();
                htmlMitEingebettetenBildern(htmlCb, bilder).then(({ html: fertig, fehlend }) => {
                  doc.execCommand('insertHTML', false, fertig);
                  sync();
                  hinweisFehlendeBilder(fehlend);
                });
                return;
              }
              if (bilder.length) {
                e.preventDefault();
                bilder.forEach((f) => bildVorbereiten(f).then((bild) => {
                  if (!bild) return;
                  doc.execCommand('insertHTML', false, bildHtml(bild));
                  sync();
                }));
                return;
              }
              const html = cd.getData('text/html');
              const text = cd.getData('text/plain');
              if (!html && !text) return;
              e.preventDefault();
              if (html) {
                const clean = html
                  .replace(/<!--[\s\S]*?-->/g, '')
                  .replace(/<\/?(?:html|head|body|meta|link|script|style|title|!doctype)[^>]*>/gi, '');
                doc.execCommand('insertHTML', false, clean);
              } else {
                const safe = text
                  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                  .replace(/\r\n|\r|\n/g, '<br>');
                doc.execCommand('insertHTML', false, safe);
              }
              sync();
            } catch { /* ignore */ }
          };
          doc.addEventListener('paste', onPasteIframe);
          // Letzte Auswahl/Cursorposition merken: Schriftart-/Größen-Auswahl und Einfügen
          // aus dem Rechtsklick-Menü wirken dann genau dort (auch wenn der Fokus kurz weg war)
          const onSelChange = () => {
            try {
              const sel = doc.getSelection();
              if (sel && sel.rangeCount) iframe._savedRange = sel.getRangeAt(0).cloneRange();
            } catch { /* ignore */ }
          };
          doc.addEventListener('selectionchange', onSelChange);
          // Rechtsklick in der Layout-Ansicht → Textbaustein-/Zwischenablage-Menü des Composers
          const onCtxIframe = (e) => {
            if (!onBodyContextMenuRef.current) return;
            e.preventDefault();
            onSelChange();
            const r = iframe.getBoundingClientRect();
            onBodyContextMenuRef.current({ x: r.left + e.clientX, y: r.top + e.clientY });
          };
          doc.addEventListener('contextmenu', onCtxIframe);
          iframe._sync = sync;
          iframe._syncDispose = () => {
            doc.removeEventListener('input', sync);
            doc.removeEventListener('blur', sync, true);
            doc.removeEventListener('paste', onPasteIframe);
            doc.removeEventListener('selectionchange', onSelChange);
            doc.removeEventListener('contextmenu', onCtxIframe);
          };
          try { iframe.contentWindow?.focus(); doc.body?.focus?.(); } catch { /* ignore */ }
        }
        // Auto-resize mit rAF-Guard gegen "ResizeObserver loop"-Fehler:
        // Höhe nur setzen, wenn sie sich spürbar (>2px) ändert.
        const fit = () => {
          const target = Math.max(
            doc.documentElement?.scrollHeight || 0,
            doc.body?.scrollHeight || 0,
            iframeEditable ? 360 : 480,
          ) + 24;
          const current = parseInt(iframe.style.height || '0', 10);
          if (Math.abs(current - target) > 2) iframe.style.height = `${target}px`;
        };
        fit();
        if (typeof ResizeObserver !== 'undefined') {
          let rafId = 0;
          const ro = new ResizeObserver(() => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => { rafId = 0; fit(); });
          });
          try { ro.observe(doc.body || doc.documentElement); } catch { /* ignore */ }
          iframe._roDispose = () => { if (rafId) cancelAnimationFrame(rafId); ro.disconnect(); };
        }
        Array.from(doc.images || []).forEach((img) => {
          if (!img.complete) img.addEventListener('load', fit, { once: true });
        });
      } catch { /* cross-origin geschützt — ignore */ }
    };
    iframe.addEventListener('load', onLoad);
    // Falls bereits geladen (kein erneuter Load) → Editierbarkeit direkt anwenden.
    try {
      const doc = iframe.contentDocument;
      if (doc && doc.readyState === 'complete') onLoad();
    } catch { /* ignore */ }
    return () => {
      iframe.removeEventListener('load', onLoad);
      try { iframe._roDispose?.(); } catch { /* noop */ }
      try { iframe._syncDispose?.(); } catch { /* noop */ }
    };
  }, [mode, iframeEditable]);

  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  const requestEditMode = () => {
    if (isLocked) {
      // WYSIWYG direkt im IFrame bearbeiten — Tabellen/Inline-Styles bleiben erhalten.
      setMode('preview');
      setIframeEditable(true);
      return;
    }
    setMode('wysiwyg');
  };

  // execCommand-Helfer für die In-Place-Bearbeitung im IFrame (wie Strato Webmail)
  const onBodyContextMenuRef = useRef(onBodyContextMenu);
  onBodyContextMenuRef.current = onBodyContextMenu;
  // Auswahl im TipTap-Editor beim Öffnen einer Auswahlliste merken (Schriftart/-größe)
  const savedTipTapSelRef = useRef(null);
  const rememberSelection = () => {
    if (editor && !richEdit) {
      const { from, to } = editor.state.selection;
      savedTipTapSelRef.current = { from, to };
    }
  };
  const tiptapChainAtSelection = () => {
    const chain = editor.chain().focus();
    const sel = savedTipTapSelRef.current;
    const max = editor.state.doc.content.size;
    return sel && sel.to <= max ? chain.setTextSelection(sel) : chain;
  };

  // Gemerkte Auswahl im IFrame wiederherstellen (Fokus war z. B. in einer Auswahlliste)
  const restoreIframeSelection = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const range = iframe?._savedRange;
    if (!doc || !range) return false;
    try {
      const sel = doc.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch { return false; }
  };

  const execCmd = (cmd, val = null) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    try {
      iframeRef.current.contentWindow?.focus();
      restoreIframeSelection();
      try { doc.execCommand('styleWithCSS', false, true); } catch { /* ignore */ }
      doc.execCommand(cmd, false, val);
      const full = doc.documentElement ? doc.documentElement.outerHTML : doc.body.innerHTML;
      iframeRef.current._lastSrc = full;
      onPristineChange?.(full);
    } catch { /* ignore */ }
  };

  // Einfügen an der Cursorposition – für Textbausteine/Zwischenablage aus dem Composer
  if (bodyApiRef) {
    bodyApiRef.current = {
      insertHtml: (html) => {
        if (richEdit) {
          const iframe = iframeRef.current;
          const doc = iframe?.contentDocument;
          if (!doc || !iframe._savedRange) return false; // ohne Cursor → Composer hängt hinten an
          try {
            iframe.contentWindow?.focus();
            restoreIframeSelection();
            doc.execCommand('insertHTML', false, html);
            iframe._sync?.();
            return true;
          } catch { return false; }
        }
        if (editor && mode === 'wysiwyg') {
          editor.chain().focus().insertContent(html).run();
          return true;
        }
        return false;
      },
    };
  }

  // Schriftgröße in der Layout-Ansicht (IFrame): execCommand kennt nur Stufen 1–7 →
  // mit Stufe 7 markieren und danach auf die gewünschte px-Größe umschreiben.
  const execFontSize = (px) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    try {
      iframeRef.current.contentWindow?.focus();
      restoreIframeSelection();
      try { doc.execCommand('styleWithCSS', false, true); } catch { /* ignore */ }
      doc.execCommand('fontSize', false, '7');
      doc.querySelectorAll('span[style*="xxx-large"], font[size="7"]').forEach((el) => {
        el.removeAttribute('size');
        el.style.fontSize = px;
      });
      const full = doc.documentElement ? doc.documentElement.outerHTML : doc.body.innerHTML;
      iframeRef.current._lastSrc = full;
      onPristineChange?.(full);
    } catch { /* ignore */ }
  };

  const toggleToolbar = () => {
    const next = !toolbarOpen;
    setToolbarOpen(next);
    try { localStorage.setItem(LS_TOOLBAR_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const insertLink = () => {
    const url = window.prompt('URL eingeben:', 'https://');
    if (!url) return;
    if (richEdit) { execCmd('createLink', url); return; }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  // Bild einfügen → Datei vom Rechner auswählen, als data:image/...;base64,... in den Editor einfügen.
  // Vorteil: Bild ist self-contained im HTML, keine externe URL nötig, funktioniert auch offline.
  const imageInputRef = useRef(null);
  const triggerImagePicker = () => imageInputRef.current?.click();
  const handleImageFileSelected = (e) => {
    const file = e.target?.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      try { window.alert('Bitte eine Bilddatei auswählen (PNG, JPG, GIF, WebP).'); } catch { /* ignore */ }
      return;
    }
    // 5 MB Limit (sonst werden Mails zu groß für viele Provider)
    if (file.size > 5 * 1024 * 1024) {
      try { window.alert('Bild zu groß. Bitte max. 5 MB verwenden.'); } catch { /* ignore */ }
      return;
    }
    bildVorbereiten(file).then((bild) => {
      if (!bild) return;
      if (richEdit) { execCmd('insertHTML', bildHtml(bild)); return; }
      editor?.chain().focus().setImage(bild).run();
    });
  };

  const Btn = ({ active, onClick, title, children, testId }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 ${
        active ? 'bg-blue-100 text-blue-700' : ''
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border-t border-b border-gray-100">
      {/* Textbaustein-Vorschlagsliste (Slash-Command) – via Portal an document.body */}
      {tbSuggest.open && createPortal(
        <div
          className="fixed z-[9999] w-64 max-h-64 overflow-y-auto rounded-lg shadow-2xl bg-white border border-gray-200 py-1"
          style={{ top: tbSuggest.top, left: tbSuggest.left }}
          data-testid="composer-tb-suggest"
        >
          <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-gray-400">
            Textbaustein · ↑↓ Enter
          </div>
          {tbSuggest.items.map((b, i) => (
            <button
              key={b.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertTbSuggest(b); }}
              className={`w-full text-left px-3 py-1.5 flex items-center gap-2 ${i === tbSuggest.index ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              data-testid={`composer-tb-suggest-${b.tb_number}`}
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-mono font-semibold shrink-0">#{b.tb_number}</span>
              <span className="text-sm text-gray-800 truncate">{b.name}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
      {/* Toolbar header (collapse toggle + mode-toggles) */}
      <div className="flex items-center justify-between gap-2 px-3 py-1 border-b border-gray-100 bg-gray-50/60">
        <button
          type="button"
          onClick={toggleToolbar}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-200 text-gray-500"
          title={toolbarOpen ? 'Werkzeugleiste einklappen' : 'Werkzeugleiste ausklappen'}
          data-testid="composer-toolbar-collapse"
        >
          {toolbarOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <div className="flex items-center gap-1 text-[11px]">
          <button
            type="button"
            onClick={requestEditMode}
            className={`px-2 py-1 rounded-md ${(mode === 'wysiwyg' && !isLocked) || richEdit ? 'bg-white border border-gray-300 text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            title="Bearbeiten (WYSIWYG — Tabellen & Stile bleiben erhalten)"
            data-testid="composer-mode-wysiwyg"
          >
            <Edit className="w-3.5 h-3.5 inline" /> Bearbeiten
          </button>
          <button
            type="button"
            onClick={() => { setIframeEditable(false); setMode('preview'); }}
            className={`px-2 py-1 rounded-md ${mode === 'preview' && !iframeEditable ? 'bg-white border border-gray-300 text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            title="Vorschau (pixelgenau wie beim Empfänger)"
            data-testid="composer-mode-preview"
          >
            <Eye className="w-3.5 h-3.5 inline" /> Vorschau
          </button>
          <button
            type="button"
            onClick={() => { setIframeEditable(false); setMode('source'); }}
            className={`px-2 py-1 rounded-md ${mode === 'source' ? 'bg-white border border-gray-300 text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            title="Quelltext"
            data-testid="composer-mode-source"
          >
            <Code2 className="w-3.5 h-3.5 inline" /> Quelltext
          </button>
        </div>
      </div>

      {/* Collapsible toolbar buttons */}
      <div
        className={`${TRANS} overflow-hidden ${toolbarOpen ? 'max-h-24' : 'max-h-0'}`}
        data-testid="composer-toolbar"
      >
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5">
          {/* Schriftart + Größe (wie im Notizbuch; mailtaugliche Schriften) */}
          <select
            value={richEdit ? '' : (editor?.getAttributes('textStyle').fontFamily || '')}
            onMouseDown={rememberSelection}
            onFocus={rememberSelection}
            onChange={(e) => {
              const v = e.target.value;
              if (richEdit) { if (v) execCmd('fontName', v); return; }
              if (!editor) return;
              if (!v) tiptapChainAtSelection().unsetFontFamily().run();
              else tiptapChainAtSelection().setFontFamily(v).run();
            }}
            disabled={fontSelectDisabled}
            className="text-xs bg-white border rounded px-1.5 h-7 outline-none hover:bg-gray-50 max-w-[130px] disabled:opacity-50"
            title={fontSelectDisabled ? 'Zum Formatieren in den Bearbeiten-Modus wechseln' : 'Schriftart (Text vorher markieren)'}
            data-testid="tb-fontfamily"
          >
            <option value="">Schriftart</option>
            {MAIL_SCHRIFTEN.map((f) => (
              <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
            ))}
          </select>
          <select
            value={richEdit ? '' : (editor?.getAttributes('textStyle').fontSize || '')}
            onMouseDown={rememberSelection}
            onFocus={rememberSelection}
            onChange={(e) => {
              const v = e.target.value;
              if (richEdit) { if (v) execFontSize(v); return; }
              if (!editor) return;
              if (!v) tiptapChainAtSelection().unsetFontSize().run();
              else tiptapChainAtSelection().setFontSize(v).run();
            }}
            disabled={fontSelectDisabled}
            className="text-xs bg-white border rounded px-1.5 h-7 outline-none hover:bg-gray-50 w-[68px] disabled:opacity-50"
            title={fontSelectDisabled ? 'Zum Formatieren in den Bearbeiten-Modus wechseln' : 'Schriftgröße (Text vorher markieren)'}
            data-testid="tb-fontsize"
          >
            <option value="">Größe</option>
            {MAIL_SCHRIFTGROESSEN.map((g) => <option key={g} value={g}>{g.replace('px', '')}</option>)}
          </select>
          <span className="w-px h-5 bg-gray-200 mx-1" />
          <Btn active={!richEdit && editor?.isActive('bold')} onClick={() => richEdit ? execCmd('bold') : editor?.chain().focus().toggleBold().run()} title="Fett" testId="tb-bold"><Bold className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive('italic')} onClick={() => richEdit ? execCmd('italic') : editor?.chain().focus().toggleItalic().run()} title="Kursiv" testId="tb-italic"><Italic className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive('underline')} onClick={() => richEdit ? execCmd('underline') : editor?.chain().focus().toggleUnderline().run()} title="Unterstreichen" testId="tb-underline"><Underline className="w-4 h-4" /></Btn>
          <span className="w-px h-5 bg-gray-200 mx-1" />
          <Btn active={!richEdit && editor?.isActive({ textAlign: 'left' })} onClick={() => richEdit ? execCmd('justifyLeft') : editor?.chain().focus().setTextAlign('left').run()} title="Linksbündig"><AlignLeft className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive({ textAlign: 'center' })} onClick={() => richEdit ? execCmd('justifyCenter') : editor?.chain().focus().setTextAlign('center').run()} title="Zentriert"><AlignCenter className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive({ textAlign: 'right' })} onClick={() => richEdit ? execCmd('justifyRight') : editor?.chain().focus().setTextAlign('right').run()} title="Rechtsbündig"><AlignRight className="w-4 h-4" /></Btn>
          <span className="w-px h-5 bg-gray-200 mx-1" />
          <Btn active={!richEdit && editor?.isActive('bulletList')} onClick={() => richEdit ? execCmd('insertUnorderedList') : editor?.chain().focus().toggleBulletList().run()} title="Aufzählung"><List className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive('orderedList')} onClick={() => richEdit ? execCmd('insertOrderedList') : editor?.chain().focus().toggleOrderedList().run()} title="Nummerierte Liste"><ListOrdered className="w-4 h-4" /></Btn>
          <span className="w-px h-5 bg-gray-200 mx-1" />
          <Btn onClick={insertLink} title="Link einfügen"><LinkIcon className="w-4 h-4" /></Btn>
          <Btn onClick={triggerImagePicker} title="Bild einfügen (Datei auswählen)" testId="tb-image"><ImageIcon className="w-4 h-4" /></Btn>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageFileSelected}
            data-testid="composer-image-input"
          />
          <span className="w-px h-5 bg-gray-200 mx-1" />
          <Btn active={!richEdit && editor?.isActive('heading', { level: 1 })} onClick={() => richEdit ? execCmd('formatBlock', 'H1') : editor?.chain().focus().toggleHeading({ level: 1 }).run()} title="Überschrift 1"><Heading1 className="w-4 h-4" /></Btn>
          <Btn active={!richEdit && editor?.isActive('heading', { level: 2 })} onClick={() => richEdit ? execCmd('formatBlock', 'H2') : editor?.chain().focus().toggleHeading({ level: 2 }).run()} title="Überschrift 2"><Heading2 className="w-4 h-4" /></Btn>
        </div>
      </div>

      {/* Locked banner: komplexes Template wird im IFrame angezeigt / bearbeitet */}
      {isLocked && (
        <div
          className="flex items-center justify-between gap-3 px-4 py-2 text-[12px] bg-amber-50 border-t border-b border-amber-200"
          data-testid="composer-locked-banner"
        >
          <div className="flex items-center gap-2 min-w-0 text-amber-900">
            {richEdit ? <Edit className="w-3.5 h-3.5 shrink-0" /> : <Lock className="w-3.5 h-3.5 shrink-0" />}
            <span className="truncate">
              {richEdit
                ? <>WYSIWYG-Bearbeitung aktiv — Tabellen, Stile &amp; Bilder bleiben <strong>vollständig erhalten</strong>.</>
                : <>HTML-Layout — wird <strong>pixelgenau</strong> mit Tabellen &amp; Stilen versendet.</>}
            </span>
          </div>
          <button
            type="button"
            onClick={richEdit ? () => { setIframeEditable(false); setMode('preview'); } : requestEditMode}
            className="text-amber-800 underline hover:text-amber-900 whitespace-nowrap"
            data-testid="composer-unlock-pristine"
          >
            {richEdit ? 'Bearbeitung beenden' : 'Bearbeiten'}
          </button>
        </div>
      )}

      {/* Editor / Preview / Source */}
      <div className="px-4 py-3 min-h-[240px] max-h-[60vh] overflow-y-auto" data-testid="composer-editor-area">
        {mode === 'wysiwyg' && !isLocked && (
          <EditorContent editor={editor} className="prose prose-sm max-w-none focus:outline-none [&_.ProseMirror]:min-h-[220px] [&_.ProseMirror]:outline-none" />
        )}
        {mode === 'preview' && (
          <iframe
            ref={iframeRef}
            title={iframeEditable ? 'E-Mail bearbeiten' : 'E-Mail-Vorschau'}
            sandbox="allow-same-origin"
            className={`w-full min-h-[360px] bg-white transition-shadow ${iframeEditable ? 'rounded-lg ring-2 ring-blue-300 border border-blue-200' : 'border-0'}`}
            data-testid="composer-iframe-preview"
          />
        )}
        {mode === 'source' && (
          <textarea
            value={pristineHtml || value}
            onChange={(e) => (isLocked ? onPristineChange?.(e.target.value) : onChange(e.target.value))}
            className="w-full min-h-[220px] font-mono text-xs p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="<p>HTML hier eingeben…</p>"
            data-testid="composer-source-textarea"
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vorlagen-Picker (Redesign 2026-05-20):
//   • Breites Modal (max-w-5xl)
//   • Sidebar-Blue Header
//   • Linke Kategorien-Liste, rechts gefilterte Vorlagen
//   • Lazy-Loading pro Kategorie (vermeidet Mehrfach-Loads)
//   • Loading-Overlay während eines Render-Calls
// ─────────────────────────────────────────────────────────────────────────────
// Im Composer werden nur aktive Vorlagen angeboten (inaktive bleiben in der Vorlagenverwaltung sichtbar)
const nurAktive = (liste) => (liste || []).filter((t) => t.is_active !== false);

export function TemplatePickerModal({ open, onClose, onSelect, recentTemplates = [] }) {
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [templatesByCat, setTemplatesByCat] = useState({});
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState(false); // beim onSelect-Call gegen Doppelklick
  const fileInputRef = useRef(null);
  // Für die Pillen (Projekt, Leadphase): Projekt- und Phasennamen einmalig beim ersten Öffnen laden
  const [pillenDaten, setPillenDaten] = useState(null);
  useEffect(() => {
    if (!open || pillenDaten) return;
    let cancel = false;
    Promise.all([
      api.get('/projects').then((r) => r.data).catch(() => ({ projects: [] })),
      settingsService.getLeadPhases().catch(() => ({ phases: [] })),
    ]).then(([p, l]) => {
      if (!cancel) setPillenDaten({ projects: p?.projects || (Array.isArray(p) ? p : []), leadPhases: l?.phases || [] });
    });
    return () => { cancel = true; };
  }, [open, pillenDaten]);

  // Favoritenstern (je Benutzer) umschalten – optimistisch in allen geladenen Ordnern
  const setzeFavorit = (id, wert) => setTemplatesByCat((prev) => {
    const next = { ...prev };
    Object.keys(next).forEach((cid) => {
      if (cid === '__all_loaded__') return;
      next[cid] = (next[cid] || []).map((t) => ((t.id || t._id) === id ? { ...t, is_favorite: wert } : t));
    });
    return next;
  });
  const toggleFavorit = async (t) => {
    const id = t.id || t._id;
    setzeFavorit(id, !t.is_favorite);
    try {
      await templateService.toggleEmailTemplateFavorit(id);
    } catch {
      setzeFavorit(id, !!t.is_favorite);
      toast.error('Favorit konnte nicht gespeichert werden');
    }
  };
  // Branchen-Erkennung: Produktlinie(n) des Mandanten → passende Vorlagen („Geeignet für“) vorschlagen
  const { user } = useAuth();
  const { active: aktiveBranchen } = useTenantIndustries();
  const produkte = useMemo(() => ermittleProduktlinien(user, aktiveBranchen), [user, aktiveBranchen]);
  const produktText = produkte.map(produktlinieLabel).join(' / ');
  const autoKategorieRef = useRef(false);
  // Breite der Kategorien-Spalte (Trennlinie verschiebbar, gespeichert)
  const [catWidth, setCatWidth] = useState(() => {
    try { const v = parseInt(localStorage.getItem('composer-template-picker-catwidth') || '', 10); return Number.isFinite(v) ? v : 224; } catch { return 224; }
  });
  const startCatResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = catWidth;
    let w = startW;
    const onMove = (ev) => { w = Math.min(480, Math.max(140, startW + ev.clientX - startX)); setCatWidth(w); };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      try { localStorage.setItem('composer-template-picker-catwidth', String(w)); } catch { /* noop */ }
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Vorlage vom lokalen Laufwerk laden (HTML/Text-Datei)
  const handleLoadFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Datei zu groß (max. 5MB)'); return; }
    try {
      const content = await file.text();
      const name = file.name.replace(/\.(html?|txt)$/i, '');
      setPicking(true);
      try {
        await onSelect({ id: 'file-' + Date.now(), name, subject: '', body_html: content, _fromFile: true });
      } finally {
        setPicking(false);
      }
    } catch {
      toast.error('Datei konnte nicht gelesen werden');
    }
  };

  // Kategorien laden
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoadingCats(true);
      try {
        const catsRaw = await templateService.getCategories('email').catch(() => []);
        const cats = Array.isArray(catsRaw) ? catsRaw : (catsRaw?.categories || []);
        if (cancel) return;
        setCategories(cats);
        // Standard: passende Vorlagen zur Branche; fällt auf „Alle“ zurück, wenn es keine gibt
        setActiveCat('passend');
        autoKategorieRef.current = true;
        setTemplatesByCat({});
      } finally {
        if (!cancel) setLoadingCats(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  // Templates für die aktive Kategorie (lazy) laden
  useEffect(() => {
    if (!open) return;
    if (activeCat === 'all' || activeCat === 'passend') {
      // Alle Templates aller Kategorien holen (1× pro geöffneter Session, parallel)
      if (templatesByCat.__all_loaded__) return;
      let cancel = false;
      setLoadingTpl(true);
      (async () => {
        try {
          const results = await Promise.all(categories.map(async (c) => {
            try {
              const r = await templateService.getEmailTemplates(c.id || c._id);
              return [c.id || c._id, nurAktive(r?.templates || r || [])];
            } catch {
              return [c.id || c._id, []];
            }
          }));
          if (cancel) return;
          const next = { __all_loaded__: true };
          results.forEach(([cid, items]) => { next[cid] = items; });
          setTemplatesByCat((prev) => ({ ...prev, ...next }));
        } finally {
          if (!cancel) setLoadingTpl(false);
        }
      })();
      return () => { cancel = true; };
    }
    if (templatesByCat[activeCat]) return; // schon geladen
    let cancel = false;
    setLoadingTpl(true);
    (async () => {
      try {
        const r = await templateService.getEmailTemplates(activeCat);
        const items = nurAktive(r?.templates || r || []);
        if (!cancel) setTemplatesByCat((prev) => ({ ...prev, [activeCat]: items }));
      } finally {
        if (!cancel) setLoadingTpl(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeCat, categories]);

  const catName = (id) => categories.find((c) => (c.id || c._id) === id)?.name || '';

  // „Zuletzt verwendet“ (die letzten 3) nur anbieten, solange die Vorlage noch existiert und aktiv ist
  const recentAktiv = useMemo(() => {
    if (!templatesByCat.__all_loaded__) return [];
    const ids = new Set();
    Object.entries(templatesByCat).forEach(([cid, items]) => {
      if (cid !== '__all_loaded__') (items || []).forEach((t) => ids.add(t.id || t._id));
    });
    return (recentTemplates || []).filter((t) => t?.id && ids.has(t.id)).slice(0, 3);
  }, [recentTemplates, templatesByCat]);

  const visibleTemplates = useMemo(() => {
    let list = [];
    if (activeCat === 'all' || activeCat === 'passend') {
      Object.entries(templatesByCat).forEach(([cid, items]) => {
        if (cid === '__all_loaded__') return;
        (items || []).forEach((t) => {
          if (!list.find((x) => (x.id || x._id) === (t.id || t._id))) {
            list.push({ ...t, _categoryName: catName(cid) });
          }
        });
      });
    } else {
      list = (templatesByCat[activeCat] || []).map((t) => ({ ...t, _categoryName: catName(activeCat) }));
    }
    list = list.map((t) => ({ ...t, _passend: passtZuMandant(t, produkte) }));
    if (activeCat === 'passend') list = list.filter((t) => t._passend);
    // Passende Vorlagen zuerst, Favoriten (Stern) stets ganz oben (Reihenfolge innerhalb bleibt erhalten)
    list = favoritenZuerst([...list.filter((t) => t._passend), ...list.filter((t) => !t._passend)]);
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q) ||
      (t._categoryName || '').toLowerCase().includes(q)
    );
  }, [activeCat, templatesByCat, search, categories, produkte]);

  // Anzahl passender Vorlagen (für den Eintrag „Passend für …“)
  const passendCount = useMemo(() => {
    if (!templatesByCat.__all_loaded__) return null;
    const ids = new Set();
    Object.entries(templatesByCat).forEach(([cid, items]) => {
      if (cid === '__all_loaded__') return;
      (items || []).forEach((t) => { if (passtZuMandant(t, produkte)) ids.add(t.id || t._id); });
    });
    return ids.size;
  }, [templatesByCat, produkte]);

  // Keine passenden Vorlagen → beim Öffnen automatisch „Alle Vorlagen“ zeigen
  useEffect(() => {
    if (autoKategorieRef.current && passendCount === 0 && activeCat === 'passend') {
      autoKategorieRef.current = false;
      setActiveCat('all');
    }
  }, [passendCount, activeCat]);

  const totalCount = useMemo(() => {
    if (!templatesByCat.__all_loaded__) return null;
    let n = 0;
    Object.entries(templatesByCat).forEach(([cid, items]) => {
      if (cid === '__all_loaded__') return;
      n += (items || []).length;
    });
    return n;
  }, [templatesByCat]);

  const counts = useMemo(() => {
    const m = {};
    Object.entries(templatesByCat).forEach(([cid, items]) => {
      if (cid === '__all_loaded__') return;
      m[cid] = (items || []).length;
    });
    return m;
  }, [templatesByCat]);

  const handlePick = async (template) => {
    if (picking) return;
    setPicking(true);
    try {
      await onSelect(template);
    } finally {
      setPicking(false);
    }
  };

  if (!open) return null;

  return (
    // Fenster-Verhalten (verschieben, Größe ändern, Vollbild) – Position/Größe werden gespeichert
    <DraggableResizableWindow
      open={open}
      onClose={onClose}
      title={(
        <span className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 shrink-0" />
          <span>Vorlage auswählen</span>
          <span className="text-xs font-normal text-gray-500 truncate">
            {totalCount !== null ? `· ${totalCount} Vorlagen in ${categories.length} Kategorien` : '· Vorlagen werden geladen …'}
          </span>
        </span>
      )}
      testid="composer-template-picker-window"
      storageKey="composer-template-picker"
      initialWidth={Math.min(1024, typeof window !== 'undefined' ? window.innerWidth - 80 : 1024)}
      initialHeight={Math.min(760, typeof window !== 'undefined' ? window.innerHeight - 80 : 700)}
      zIndex={300}
    >
      <div className="h-full flex flex-col relative" data-testid="composer-template-picker">

        {/* Quelle wählen: Laufwerk-Datei + Suche in EasyWork-Vorlagen */}
        <div className="px-5 py-2 border-b border-gray-100 bg-gray-50/40 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="In EasyWork-Vorlagen suchen (Name, Betreff, Kategorie) …"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-white"
              data-testid="composer-template-search"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,.txt"
            onChange={handleLoadFromFile}
            className="hidden"
            data-testid="composer-template-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={picking}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 transition-colors"
            data-testid="composer-template-from-file"
            title="HTML-/Text-Vorlage vom lokalen Laufwerk laden"
          >
            <HardDrive className="w-4 h-4" />
            Vom Laufwerk
          </button>
        </div>

        {/* Body: Kategorien-Sidebar + Templates */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Kategorien-Spalte */}
          <aside className="shrink-0 border-r border-gray-200 bg-gray-50/30 overflow-y-auto" style={{ width: catWidth }} data-testid="composer-template-categories">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Kategorien</div>
            <ul className="px-2 pb-2 space-y-0.5">
              <li>
                <button
                  type="button"
                  onClick={() => { autoKategorieRef.current = false; setActiveCat('passend'); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between gap-2 ${
                    activeCat === 'passend'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-emerald-800 hover:bg-emerald-50'
                  }`}
                  title={`Vorlagen, die für ${produktText} geeignet sind`}
                  data-testid="composer-template-cat-passend"
                >
                  <span className="flex items-center gap-1.5 min-w-0"><Sparkles className="w-3.5 h-3.5 shrink-0" /><span className="truncate">Passend zur Branche</span></span>
                  {passendCount !== null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                      activeCat === 'passend' ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'
                    }`}>{passendCount}</span>
                  )}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => { autoKategorieRef.current = false; setActiveCat('all'); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                    activeCat === 'all'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid="composer-template-cat-all"
                >
                  <span>Alle Vorlagen</span>
                  {totalCount !== null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      activeCat === 'all' ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>{totalCount}</span>
                  )}
                </button>
              </li>
              {loadingCats ? (
                <li className="px-3 py-3 text-xs text-gray-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Lade Kategorien…</li>
              ) : categories.length === 0 ? (
                <li className="px-3 py-2 text-xs text-gray-400">Keine Kategorien hinterlegt.</li>
              ) : (
                categories.map((c) => {
                  const cid = c.id || c._id;
                  const active = activeCat === cid;
                  const cnt = counts[cid];
                  return (
                    <li key={cid}>
                      <button
                        type="button"
                        onClick={() => setActiveCat(cid)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between gap-2 ${
                          active
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid={`composer-template-cat-${cid}`}
                      >
                        <span className="truncate">{c.name}</span>
                        {typeof cnt === 'number' && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                            active ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>{cnt}</span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </aside>
          <div
            onMouseDown={startCatResize}
            onDoubleClick={() => { setCatWidth(224); try { localStorage.removeItem('composer-template-picker-catwidth'); } catch { /* noop */ } }}
            className="w-1.5 shrink-0 -ml-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors z-10"
            title="Ziehen: Breite ändern · Doppelklick: Standard"
            data-testid="composer-template-divider"
          />

          {/* Templates */}
          <div className="flex-1 overflow-y-auto relative">
            {recentAktiv.length > 0 && !search && (
              <div className="px-5 py-2.5 border-b border-amber-100 bg-amber-50/60 space-y-1.5" data-testid="composer-template-last-used-bar">
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Zuletzt verwendet</div>
                {recentAktiv.map((rt) => (
                  <button
                    key={rt.id}
                    type="button"
                    onClick={() => handlePick(rt)}
                    disabled={picking}
                    className="w-full text-left px-3 py-2 rounded-lg bg-white border border-amber-200 hover:bg-amber-50 flex items-center gap-2.5 disabled:opacity-50 transition-colors"
                    data-testid={`composer-template-last-used-${rt.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{rt.name}</div>
                      {rt.subject && (
                        <div className="text-[11px] text-gray-500 truncate">Betreff: {rt.subject}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {loadingTpl && (
              <div className="absolute top-0 left-0 right-0 bg-white/80 backdrop-blur-sm py-2 text-center text-xs text-gray-500 flex items-center justify-center gap-2 z-10 border-b border-gray-100">
                <Loader2 className="w-3 h-3 animate-spin" /> Vorlagen werden geladen …
              </div>
            )}
            {!loadingTpl && visibleTemplates.length === 0 ? (
              <div className="text-center py-16 text-sm text-gray-400">
                {search ? `Keine Treffer für „${search}".` : (activeCat === 'passend' ? `Keine Vorlagen für ${produktText} gekennzeichnet – unter „Alle Vorlagen“ sind alle wählbar.` : 'Keine Vorlagen in dieser Kategorie.')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {visibleTemplates.map((t) => (
                  <li key={t.id || t._id} className="relative">
                    <FavoritStern aktiv={t.is_favorite} onToggle={() => toggleFavorit(t)} className="absolute right-3 top-3 z-10"
                      testId={`composer-template-favorit-${t.id || t._id}`} />
                    <button
                      type="button"
                      onClick={() => handlePick(t)}
                      disabled={picking}
                      className="w-full text-left pl-5 pr-12 py-3 hover:bg-blue-50 flex items-start gap-3 disabled:opacity-50 transition-colors"
                      data-testid={`composer-template-item-${t.id || t._id}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 truncate">{t.name}</div>
                        {t.subject && (
                          <div className="text-xs text-gray-600 truncate mt-0.5">Betreff: {t.subject}</div>
                        )}
                        <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                            {t._categoryName || 'Ohne Kategorie'}
                          </span>
                          {t._passend && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" data-testid={`composer-template-passend-${t.id || t._id}`}>Passend</span>
                          )}
                        </div>
                        <VorlagenPillen template={t} projects={pillenDaten?.projects || []} leadPhases={pillenDaten?.leadPhases || []} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer mit Schließen */}
        <div className="px-5 py-2.5 border-t border-gray-200 bg-gray-50/60 flex items-center justify-between gap-2">
          <div className="text-[11px] text-gray-500">
            {visibleTemplates.length > 0 ? `${visibleTemplates.length} angezeigt` : ''}
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
            Abbrechen
          </button>
        </div>

        {picking && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-20" data-testid="composer-template-applying">
            <div className="flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> Vorlage wird übernommen …
            </div>
          </div>
        )}
      </div>
    </DraggableResizableWindow>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hauptkomponente
// ─────────────────────────────────────────────────────────────────────────────
export default function EmailComposerV2({
  open,
  onClose,
  addressId,
  addressEmail,
  addressName,
  contactPersons = [],
  orderId = null,
  projectId = null,
  initialAttachments = [],
  initialSubject = '',
  initialBodyHtml = '',
  recipient = null,
  initialToEmails = null,
  initialBccEmails = null,
  defaultSubject = '',
  defaultMessage = '',
  appointmentId = null,
  initialTemplateId = null,
  initialTemplateName = '',
  onSent,
  // Serienmail aus der Adressliste: [{ address_id, email }] – nach dem Versand je Adresse protokollieren
  serienEmpfaenger = null,
}) {
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  // Wenn der User via TabBar auf einen anderen Tab (z. B. Adress-Tab) wechselt,
  // wird der Composer ausgeblendet – Inhalte bleiben aber erhalten. Beim Klick
  // auf den „E-Mail schreiben"-Tab wird er wieder eingeblendet.
  const [hiddenByTab, setHiddenByTab] = useState(false);
  // TabContext ist optional (in einigen Test-/Standalone-Views nicht vorhanden).
  const tabsApi = useContext(TabContext);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [docPicker, setDocPicker] = useState({ open: false, mode: 'crm' });

  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  // Inhalt als WhatsApp-Bild (PNG) ausgeben – für Kontakte, die lieber per WhatsApp kommunizieren
  const [whatsAppBildOffen, setWhatsAppBildOffen] = useState(false);
  // pristineHtml: Bei komplexen HTML-Templates (Tabellen, Inline-Styles,
  // background-colors, Bilder) speichern wir das ORIGINAL hier. Solange gesetzt,
  // wird im Editor nur eine IFrame-Vorschau gezeigt und das ungetrübte HTML wird
  // beim Versand verwendet — damit der Empfänger das Layout pixelgenau sieht.
  const [pristineHtml, setPristineHtml] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState('none');
  const [signatureMenuOpen, setSignatureMenuOpen] = useState(false);

  // Textbausteine
  const [textBlocks, setTextBlocks] = useState([]);
  const [tbMenuOpen, setTbMenuOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0 });
  // Einfüge-Schnittstelle des Editors (Cursorposition, auch in der Layout-Ansicht)
  const bodyApiRef = useRef(null);
  // Closure-sichere Liste der Textbausteine für Editor-Callbacks (TB-Vorschläge)
  const textBlocksRef = useRef([]);
  useEffect(() => { textBlocksRef.current = textBlocks; }, [textBlocks]);

  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('smtp');
  const [selectedOutlookAccount, setSelectedOutlookAccount] = useState('');
  const [selectedSmtpAccount, setSelectedSmtpAccount] = useState('');
  const [fromEmail, setFromEmail] = useState('');

  // Per-Nutzer-Persistenz (zuletzt gewählter Absender + zuletzt genutzte Vorlage)
  const userKey = useMemo(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.user_id || u.email || 'anon'; }
    catch { return 'anon'; }
  }, []);
  const LS_SENDER = `composer:lastSender:${userKey}`;
  const LS_TEMPLATE = `composer:lastTemplate:${userKey}`;
  // Die letzten 3 gewählten Vorlagen (neueste zuerst); Altbestand aus LS_TEMPLATE übernehmen
  const LS_RECENT_TEMPLATES = `composer:recentTemplates:${userKey}`;
  const [recentTemplates, setRecentTemplates] = useState(() => {
    try {
      const list = JSON.parse(localStorage.getItem(`composer:recentTemplates:${userKey}`) || 'null');
      if (Array.isArray(list)) return list;
      const last = JSON.parse(localStorage.getItem(`composer:lastTemplate:${userKey}`) || 'null');
      return last?.id ? [last] : [];
    } catch { return []; }
  });

  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  // In dieser Nachricht verwendete Vorlage (für Vertriebs-Journal / Korrespondenz-Log)
  const [usedTemplate, setUsedTemplate] = useState(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const [priority, setPriority] = useState('normal');
  const [requestReadReceipt, setRequestReadReceipt] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null); // ISO string

  // ─── Projekt-Verknüpfung (Beteiligter / Historie) ───
  // Nutzer öffnet einen der beiden Footer-Buttons → Popover mit KI-Vorschlägen.
  // Beim Senden führt handleSend die Aktion(en) für das gewählte Projekt aus.
  const LS_LINKED_PROJECT = `composer:linkedProject:${userKey}`;
  const [projLinkOpen, setProjLinkOpen] = useState(false);
  const [projLinkMode, setProjLinkMode] = useState('participant'); // welcher Button das Popover geöffnet hat
  const [projSuggestions, setProjSuggestions] = useState([]);
  const [projSuggestLoading, setProjSuggestLoading] = useState(false);
  const [projSearchAll, setProjSearchAll] = useState([]);
  const [projSearchQuery, setProjSearchQuery] = useState('');
  const [linkedProject, setLinkedProject] = useState(null); // { project_id, name, ... }
  const [linkAsParticipant, setLinkAsParticipant] = useState(false);
  const [logInProjectHistory, setLogInProjectHistory] = useState(false);
  const [matchedAddressId, setMatchedAddressId] = useState(null);
  const [autoLinked, setAutoLinked] = useState(false); // wurde Historie automatisch aktiviert?
  // Anhang-Ablage
  const [attachAttachments, setAttachAttachments] = useState(false);
  const [attachmentTarget, setAttachmentTarget] = useState('beleg'); // 'beleg' | 'document'

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('to');
  const [pickerModus, setPickerModus] = useState('adressbuch');

  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  // „Anweisung an die KI“ – wird nie versendet, steuert nur den KI-Entwurf (mit CRM-Wissen zum Empfänger)
  const [kiAnweisung, setKiAnweisung] = useState('');

  // ─── Drag & Resize: Geometrie ───
  const LS_GEO_KEY = 'emailComposerV2Geometry';
  const DEFAULT_GEO = (() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const width = Math.min(760, Math.max(560, Math.floor(vw * 0.55)));
    const height = Math.min(Math.floor(vh * 0.88), 980);
    return {
      x: Math.max(20, Math.floor((vw - width) / 2)),
      y: Math.max(20, Math.floor((vh - height) / 2)),
      width,
      height,
    };
  });
  const [geo, setGeo] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_GEO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.width && parsed.height) return { ...DEFAULT_GEO(), ...parsed };
      }
    } catch { /* ignore */ }
    return DEFAULT_GEO();
  });

  const persistGeo = useCallback((next) => {
    try { localStorage.setItem(LS_GEO_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }, []);

  const clampToViewport = (g) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minW = 420;
    const minH = 360;
    const w = Math.max(minW, Math.min(g.width, vw - 20));
    const h = Math.max(minH, Math.min(g.height, vh - 20));
    const x = Math.max(0, Math.min(g.x, vw - w));
    const y = Math.max(0, Math.min(g.y, vh - h));
    return { x, y, width: w, height: h };
  };

  // Drag-Handler (title bar)
  const dragStateRef = useRef(null);
  const onTitleMouseDown = (e) => {
    if (maximized) return;
    if (e.button !== 0) return;
    // Buttons in der Titelleiste nicht als Drag-Auslöser zulassen
    if (e.target.closest('button')) return;
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: geo.x,
      origY: geo.y,
    };
    e.preventDefault();
    document.body.style.userSelect = 'none';
  };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragStateRef.current;
      if (!d) return;
      // Werte jetzt auslesen: der Updater läuft erst beim Rendern – dann kann mouseup
      // den Ref schon geleert haben (Absturz „reading 'origX'“)
      const x = d.origX + (e.clientX - d.startX);
      const y = d.origY + (e.clientY - d.startY);
      setGeo((prev) => clampToViewport({ ...prev, x, y }));
    };
    const onUp = () => {
      if (dragStateRef.current) {
        dragStateRef.current = null;
        document.body.style.userSelect = '';
        // persist
        setGeo((cur) => { persistGeo(cur); return cur; });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persistGeo]);

  // Resize-Handler (bottom-right)
  const resizeStateRef = useRef(null);
  const onResizeMouseDown = (e) => {
    if (maximized) return;
    if (e.button !== 0) return;
    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origW: geo.width,
      origH: geo.height,
    };
    e.preventDefault();
    e.stopPropagation();
    document.body.style.userSelect = 'none';
  };
  useEffect(() => {
    const onMove = (e) => {
      const r = resizeStateRef.current;
      if (!r) return;
      const width = r.origW + (e.clientX - r.startX);
      const height = r.origH + (e.clientY - r.startY);
      setGeo((prev) => clampToViewport({ ...prev, width, height }));
    };
    const onUp = () => {
      if (resizeStateRef.current) {
        resizeStateRef.current = null;
        document.body.style.userSelect = '';
        setGeo((cur) => { persistGeo(cur); return cur; });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persistGeo]);

  // Beim Resize/Rotate des Browsers ggf. clampen
  useEffect(() => {
    const onResize = () => setGeo((cur) => clampToViewport(cur));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const editorRef = useRef(null);
  const editorModeRef = useRef(null);

  // Fügt HTML an der Cursorposition (TipTap) bzw. am Ende (komplexe IFrame-Vorlage) ein
  const insertHtmlIntoBody = useCallback((html) => {
    if (!html) return;
    // Bevorzugt an der Cursorposition (normaler Editor und bearbeitbare Layout-Ansicht)
    if (bodyApiRef.current?.insertHtml?.(html)) return;
    if (pristineHtml) {
      setPristineHtml((prev) => (prev || '') + html);
      setBodyHtml((prev) => (prev || '') + html);
      return;
    }
    const ed = editorRef.current;
    if (ed) {
      ed.chain().focus().insertContent(html).run();
    } else {
      setBodyHtml((prev) => (prev || '') + html);
    }
  }, [pristineHtml]);

  // Löst {{platzhalter}} via Backend auf (Kontakt = addressId), behält unbekannte Tokens
  const resolveSnippet = useCallback(async (html) => {
    if (!html || !html.includes('{{')) return html;
    try {
      const res = await templateService.renderSnippet({ html, address_id: addressId || null });
      return res?.rendered || html;
    } catch {
      return html;
    }
  }, [addressId]);
  // Closure-sichere Variante für den Editor (Slash-Command-Einfügung)
  const resolveSnippetRef = useRef(null);
  useEffect(() => { resolveSnippetRef.current = resolveSnippet; }, [resolveSnippet]);

  const insertTextBlock = useCallback(async (block) => {
    setTbMenuOpen(false);
    setCtxMenu((c) => ({ ...c, open: false }));
    const html = await resolveSnippet(block?.body_html || '');
    insertHtmlIntoBody(html);
  }, [insertHtmlIntoBody, resolveSnippet]);

  const insertSignatureToBody = useCallback(async (sig) => {
    setSignatureMenuOpen(false);
    setSelectedSignature('none'); // verhindert doppelte Signatur beim Versand
    const raw = sig?.signature_html || sig?.content_html || sig?.html || '';
    if (!raw) return;
    const html = await resolveSnippet(raw);
    insertHtmlIntoBody(`<br>${html}`);
  }, [insertHtmlIntoBody, resolveSnippet]);

  const handleEditorContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY });
  };

  // Rechtsklick → „Aus Zwischenablage einfügen“ (Text, formatierter Text oder Bild)
  const pasteFromClipboard = useCallback(async () => {
    setCtxMenu((c) => ({ ...c, open: false }));
    const cb = navigator.clipboard;
    if (!cb || (!cb.read && !cb.readText)) {
      toast.info('Der Browser erlaubt hier keinen Zugriff auf die Zwischenablage – bitte STRG+V verwenden.');
      return;
    }
    const textToHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r\n|\r|\n/g, '<br>');
    try {
      if (cb.read) {
        const items = await cb.read();
        for (const item of items) {
          const imgType = item.types.find((t) => t.startsWith('image/'));
          if (imgType) {
            const blob = await item.getType(imgType);
            const bild = await bildVorbereiten(new File([blob], 'zwischenablage', { type: imgType }));
            if (bild) insertHtmlIntoBody(bildHtml(bild));
            return;
          }
          if (item.types.includes('text/html')) {
            const html = await (await item.getType('text/html')).text();
            insertHtmlIntoBody(html
              .replace(/<!--[\s\S]*?-->/g, '')
              .replace(/<\/?(?:html|head|body|meta|link|script|style|title|!doctype)[^>]*>/gi, ''));
            return;
          }
          if (item.types.includes('text/plain')) {
            insertHtmlIntoBody(textToHtml(await (await item.getType('text/plain')).text()));
            return;
          }
        }
        toast.info('Die Zwischenablage ist leer.');
      } else {
        const t = await cb.readText();
        if (t) insertHtmlIntoBody(textToHtml(t)); else toast.info('Die Zwischenablage ist leer.');
      }
    } catch {
      toast.error('Kein Zugriff auf die Zwischenablage – bitte im Browser erlauben oder STRG+V verwenden.');
    }
  }, [insertHtmlIntoBody]);

  // ─── Init ───
  useEffect(() => {
    if (!open) return;
    // Reset
    const initialTo = (Array.isArray(initialToEmails) && initialToEmails.length)
      ? initialToEmails
      : recipient ? [recipient] : addressEmail ? [addressEmail] : [];
    setToEmails(initialTo);
    setCcEmails([]);
    const initBcc = Array.isArray(initialBccEmails) ? initialBccEmails.filter(Boolean) : [];
    setBccEmails(initBcc);
    setCcVisible(false);
    setBccVisible(initBcc.length > 0);
    setSubject(initialSubject || defaultSubject || '');
    setKiAnweisung('');
    const _initBody = initialBodyHtml || defaultMessage || '';
    setBodyHtml(_initBody);
    // Komplexe HTML-Vorlagen (Inline-Styles, Tabellen) im IFrame zeigen, statt TipTap-Stripping
    setPristineHtml(_isComplexHtml(_initBody) ? _initBody : null);
    setAttachments(initialAttachments || []);
    setSelectedSignature('none');
    setPriority('normal');
    setRequestReadReceipt(false);
    setScheduledAt(null);
    setMinimized(false);
    setMaximized(false);
    setHiddenByTab(false);
    // Projekt-Verknüpfung: Sticky-State aus localStorage laden, sonst frisch
    try {
      const raw = localStorage.getItem(LS_LINKED_PROJECT);
      const stored = raw ? JSON.parse(raw) : null;
      if (stored && stored.project_id) {
        setLinkedProject(stored);
        setLogInProjectHistory(true); // Historie ist Default bei Sticky-Wahl
      } else {
        setLinkedProject(null);
      }
    } catch { setLinkedProject(null); }
    setProjLinkOpen(false);
    setLinkAsParticipant(false);
    setProjSuggestions([]);
    setMatchedAddressId(null);
    setProjSearchQuery('');
    setAutoLinked(false);
    setAttachAttachments(false);
    setAttachmentTarget('beleg');
    // Load services
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ─── Tab-Integration: virtuellen „E-Mail schreiben"-Tab in der TabBar registrieren ───
  useEffect(() => {
    if (!open || !tabsApi?.openVirtualTab) return;
    tabsApi.openVirtualTab({
      id: 'email-composer',
      title: 'E-Mail schreiben',
      icon: 'Mail',
      onActivate: () => setHiddenByTab(false),
      onDeactivate: () => setHiddenByTab(true),
      onClose: () => { onClose && onClose(); },
    });
    return () => {
      // Beim Unmount des Composers Tab still entfernen (ohne onClose zu triggern).
      if (tabsApi?.removeVirtualTab) {
        try { tabsApi.removeVirtualTab('email-composer'); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tab-Titel dynamisch anpassen, sobald Betreff eingegeben wird.
  useEffect(() => {
    if (!open || !tabsApi?.updateVirtualTabTitle) return;
    const title = subject && subject.trim() ? `📧 ${subject.trim().slice(0, 30)}` : 'E-Mail schreiben';
    tabsApi.updateVirtualTabTitle('email-composer', title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, open]);

  const loadServices = async () => {
    try {
      const [outlookRes, sigRes, statusRes, smtpRes, tbRes] = await Promise.all([
        outlookService.getAccounts().catch(() => ({ accounts: [] })),
        templateService.getSignatures().catch(() => []),
        emailService.getStatus().catch(() => null),
        emailService.getEmailAccounts().catch(() => ({ accounts: [] })),
        templateService.getTextBlocks(true).catch(() => ({ text_blocks: [] })),
      ]);
      setOutlookAccounts(outlookRes.accounts || []);
      const sortedTb = (tbRes?.text_blocks || []).slice().sort((a, b) => {
        const na = parseInt(a.tb_number, 10);
        const nb = parseInt(b.tb_number, 10);
        const aNum = !Number.isNaN(na);
        const bNum = !Number.isNaN(nb);
        if (aNum && bNum) return na - nb;
        if (aNum) return -1;
        if (bNum) return 1;
        return String(a.tb_number).localeCompare(String(b.tb_number));
      });
      setTextBlocks(sortedTb);
      const smtpAccs = smtpRes.accounts || [];
      setSmtpAccounts(smtpAccs);
      const sigs = Array.isArray(sigRes) ? sigRes : (sigRes?.signatures || []);
      setSignatures(sigs);
      const defaultSig = sigs.find((s) => s.is_default) || null;
      if (defaultSig) setSelectedSignature(defaultSig.id || defaultSig._id || 'none');
      // Standard-Absender: zuletzt gewählter (falls noch vorhanden) > Default-SMTP > erstes SMTP > Outlook > Env-SMTP
      const defSmtp = smtpAccs.find((a) => a.is_default) || smtpAccs[0] || null;
      const savedKey = (() => { try { return localStorage.getItem(LS_SENDER); } catch { return null; } })();
      const optList = [
        ...smtpAccs.map((a) => ({ key: `smtp:${a.id}`, provider: 'smtp', accountId: a.id, email: a.email })),
        ...(outlookRes.accounts || []).map((a) => ({ key: `outlook:${a.id}`, provider: 'outlook', accountId: a.id, email: a.email })),
      ];
      const savedOpt = savedKey ? optList.find((o) => o.key === savedKey) : null;
      if (savedOpt) {
        setSelectedProvider(savedOpt.provider);
        if (savedOpt.provider === 'outlook') { setSelectedOutlookAccount(savedOpt.accountId); setSelectedSmtpAccount(''); }
        else { setSelectedSmtpAccount(savedOpt.accountId); setSelectedOutlookAccount(''); }
        setFromEmail(savedOpt.email);
      } else if (defSmtp) {
        setSelectedProvider('smtp');
        setSelectedSmtpAccount(defSmtp.id);
        setFromEmail(defSmtp.email);
      } else if ((!statusRes || !statusRes.configured) && (outlookRes.accounts || []).length > 0) {
        setSelectedProvider('outlook');
        setSelectedOutlookAccount(outlookRes.accounts[0].id);
        setFromEmail(outlookRes.accounts[0].email);
      } else if (statusRes?.sender_email) {
        setSelectedProvider('smtp');
        setSelectedSmtpAccount('');
        setFromEmail(statusRes.sender_email);
      }
    } catch { /* silent */ }
  };

  // Vereinheitlichte Absender-Auswahlliste (SMTP-Konten + Outlook-Konten)
  const senderOptions = useMemo(() => {
    const opts = [];
    smtpAccounts.forEach((a) => {
      opts.push({ key: `smtp:${a.id}`, provider: 'smtp', accountId: a.id, email: a.email, name: a.name });
    });
    outlookAccounts.forEach((a) => {
      opts.push({ key: `outlook:${a.id}`, provider: 'outlook', accountId: a.id, email: a.email, name: a.name || a.email });
    });
    // Env-SMTP-Standardabsender (z.B. SuperAdmin ohne eigenes Konto)
    if (smtpAccounts.length === 0 && fromEmail) {
      opts.push({ key: 'smtp:env', provider: 'smtp', accountId: '', email: fromEmail, name: fromEmail });
    }
    return opts;
  }, [smtpAccounts, outlookAccounts, fromEmail]);

  const selectedSenderKey = useMemo(() => {
    if (selectedProvider === 'outlook' && selectedOutlookAccount) return `outlook:${selectedOutlookAccount}`;
    if (selectedSmtpAccount) return `smtp:${selectedSmtpAccount}`;
    return senderOptions[0]?.key || '';
  }, [selectedProvider, selectedOutlookAccount, selectedSmtpAccount, senderOptions]);

  const handleSenderChange = (key) => {
    const opt = senderOptions.find((o) => o.key === key);
    if (!opt) return;
    setSelectedProvider(opt.provider);
    if (opt.provider === 'outlook') {
      setSelectedOutlookAccount(opt.accountId);
      setSelectedSmtpAccount('');
    } else {
      setSelectedSmtpAccount(opt.accountId);
      setSelectedOutlookAccount('');
    }
    setFromEmail(opt.email);
    try { localStorage.setItem(LS_SENDER, key); } catch { /* ignore */ }
  };

  const fromLabel = useMemo(() => {
    if (selectedProvider === 'outlook' && selectedOutlookAccount) {
      const acc = outlookAccounts.find((a) => a.id === selectedOutlookAccount);
      return acc?.email || 'Outlook-Konto';
    }
    return fromEmail || 'SMTP-Standardabsender';
  }, [selectedProvider, selectedOutlookAccount, outlookAccounts, fromEmail]);

  // #3 — Alle E-Mail-Adressen dieses Adressdatensatzes (Hauptadresse + Ansprechpartner)
  const addressEmailOptions = useMemo(() => {
    const out = [];
    if (addressEmail) out.push({ email: addressEmail, label: addressName || addressEmail });
    (contactPersons || []).forEach((cp) => {
      if (cp.email) {
        const nm = [cp.first_name, cp.last_name].filter(Boolean).join(' ').trim() || 'Ansprechpartner';
        out.push({ email: cp.email, label: nm, sub: cp.position || 'Ansprechpartner' });
      }
    });
    const seen = new Set();
    return out.filter((o) => { const k = o.email.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [addressEmail, addressName, contactPersons]);

  const toggleAddressEmail = (email) => {
    setToEmails((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  };

  // ─── Template Select ───
  const handleSelectTemplate = async (template) => {
    // Vorlage vom lokalen Laufwerk: kein Backend-Render (keine ID), Inhalt direkt übernehmen
    if (template?._fromFile) {
      const html = template.body_html || '';
      const complex = _isComplexHtml(html);
      setPristineHtml(complex ? html : null);
      setBodyHtml(html);
      if (template.subject) setSubject(template.subject);
      if (complex && editorModeRef.current?.setMode) {
        editorModeRef.current.setMode('preview');
      }
      toast.success(`Vorlage „${template.name}" vom Laufwerk geladen`);
      setUsedTemplate({ id: null, name: template.name });
      setTemplateModalOpen(false);
      return;
    }
    try {
      const rendered = await templateService.renderEmail({
        template_id: template.id || template._id,
        address_id: addressId || undefined,
        order_id: orderId || undefined,
        project_id: projectId || undefined,
      });
      setSubject(rendered.subject || subject);
      const html = rendered.body_html || '';
      const complex = _isComplexHtml(html);
      // Komplexe Templates (Tabellen, Inline-Styles, Bilder) im "Pristine-Lock":
      // - bodyHtml + pristineHtml beide gesetzt → IFrame-Preview, kein TipTap-Stripping
      // - Senden nutzt pristineHtml → Empfänger sieht Layout pixelgenau
      setPristineHtml(complex ? html : null);
      setBodyHtml(html);
      if (complex && editorModeRef.current?.setMode) {
        editorModeRef.current.setMode('preview');
      }
      if (rendered.attachments?.length) {
        const tplAtt = rendered.attachments.map((a) => ({
          id: `tpl-${Date.now()}-${a.filename}`,
          name: a.filename,
          size: a.file_size || 0,
          type: a.file_type || a.content_type || 'application/octet-stream',
          base64: a.file_data || a.content,
          fromTemplate: true,
        }));
        setAttachments((prev) => [...prev, ...tplAtt]);
        toast.info(`${tplAtt.length} Anhang(e) aus Vorlage übernommen`);
      }
      toast.success(`Vorlage „${template.name}" geladen`);
      // Zuletzt genutzte Vorlage je Nutzer merken
      const lt = { id: template.id || template._id, name: template.name, subject: template.subject || rendered.subject || '', categoryId: template.category_id || template._categoryId || null };
      const recent = [lt, ...recentTemplates.filter((t) => t?.id !== lt.id)].slice(0, 3);
      try {
        localStorage.setItem(LS_TEMPLATE, JSON.stringify(lt));
        localStorage.setItem(LS_RECENT_TEMPLATES, JSON.stringify(recent));
      } catch { /* ignore */ }
      setRecentTemplates(recent);
      setUsedTemplate({ id: lt.id, name: lt.name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vorlage konnte nicht geladen werden');
    } finally {
      setTemplateModalOpen(false);
    }
  };

  // Vorlage vorwählen (z. B. „nächste Vorlage“ aus der Bearbeitungsliste im Vertriebscockpit) – einmal je Öffnen
  const vorgewaehlteVorlageRef = useRef(null);
  useEffect(() => {
    if (!open || !initialTemplateId || vorgewaehlteVorlageRef.current === initialTemplateId) return;
    vorgewaehlteVorlageRef.current = initialTemplateId;
    handleSelectTemplate({ id: initialTemplateId, name: initialTemplateName || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTemplateId]);

  // ─── Anhänge ───
  const handleAttachmentFiles = (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const MAX = 20 * 1024 * 1024;
    for (const f of files) {
      if (f.size > MAX) { toast.error(`„${f.name}" ist zu groß (max. 20 MB)`); return; }
    }
    const total = files.reduce((s, f) => s + f.size, 0) + attachments.reduce((s, a) => s + a.size, 0);
    if (total > MAX) { toast.error('Maximale Gesamtgröße der Anhänge überschritten (20 MB)'); return; }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result?.split(',')[1];
        if (base64) {
          setAttachments((prev) => [...prev, {
            id: `${Date.now()}-${file.name}`,
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream',
            base64,
          }]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Anhänge aus CRM/Projekt-Dokumenten (per Download -> Base64) übernehmen
  const addDocumentsAsAttachments = async (selectedDocs) => {
    if (!selectedDocs?.length) return;
    const MAX = 20 * 1024 * 1024;
    let added = 0;
    for (const doc of selectedDocs) {
      const docName = doc.name || doc.title || doc.original_filename || doc.filename || 'dokument';
      try {
        const res = await apiFetch(`/documents/${doc.id}/download`);
        if (!res.ok) {
          let backendMsg = '';
          try { const j = await res.json(); backendMsg = j?.detail || ''; } catch { /* ignore */ }
          const msg = backendMsg || `HTTP ${res.status}`;
          toast.error(`„${docName}" konnte nicht angehängt werden: ${msg}`);
          continue;
        }
        const blob = await res.blob();
        if (blob.size > MAX) { toast.error(`„${docName}" ist zu groß (max. 20 MB)`); continue; }
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = (e) => resolve(e.target?.result?.split(',')[1] || null);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        if (!base64) continue;
        setAttachments((prev) => {
          if (prev.some((a) => a.name === docName && a.size === blob.size)) return prev; // Duplikat vermeiden
          return [...prev, {
            id: `${Date.now()}-${docName}`,
            name: docName,
            size: blob.size,
            type: blob.type || doc.mime_type || doc.content_type || 'application/octet-stream',
            base64,
          }];
        });
        added += 1;
      } catch (e) {
        toast.error(`„${docName}" konnte nicht angehängt werden: ${e?.message || 'Unbekannter Fehler'}`);
      }
    }
    if (added) toast.success(`${added} Dokument(e) angehängt`);
  };

  // ─── KI-Aktionen ───
  // Backend: POST /api/ai/email-assist (Prompt baut der Server; eingebettete Bilder bleiben erhalten)
  const runAi = async (action) => {
    setAiMenuOpen(false);
    // In der Layout-Ansicht (Antwort/Vorlage) ist pristineHtml der angezeigte Text
    const aktuell = pristineHtml || bodyHtml || '';
    const hatText = aktuell.replace(/<[^>]*>|&nbsp;/g, '').trim().length > 0;
    if (action !== 'write' && !hatText) {
      toast.error('Bitte gib zuerst etwas Text ein, den die KI bearbeiten kann.');
      return;
    }
    if (action === 'write' && !hatText && !subject) {
      toast.error('Bitte gib einen Betreff oder Stichpunkte ein.');
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post('/ai/email-assist', { action, subject, body: aktuell }, { timeout: 120000 });
      if (!data?.body) throw new Error('leer');
      if (pristineHtml) setPristineHtml(_isComplexHtml(data.body) ? data.body : null);
      setBodyHtml(data.body);
      toast.success('KI-Vorschlag übernommen');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KI-Assistent: Anfrage fehlgeschlagen');
    } finally {
      setAiBusy(false);
    }
  };

  // „Anweisung an die KI“: personalisierter Entwurf mit CRM-Wissen zum (ersten) Empfänger
  const runKiAnweisung = async () => {
    const anweisung = kiAnweisung.trim();
    if (!anweisung) {
      toast.error('Bitte eine Anweisung an die KI eingeben.');
      return;
    }
    const vorherBody = bodyHtml;
    const vorherPristine = pristineHtml;
    const vorherBetreff = subject;
    const aktuell = pristineHtml || bodyHtml || '';
    setAiBusy(true);
    try {
      const { data } = await api.post('/ai/email-assist', {
        action: 'instruct',
        instruction: anweisung,
        subject,
        body: aktuell,
        address_id: addressId || matchedAddressId || null,
        recipient_email: toEmails[0] || null,
      }, { timeout: 120000 });
      if (!data?.body) throw new Error('leer');
      if (pristineHtml) setPristineHtml(_isComplexHtml(data.body) ? data.body : null);
      setBodyHtml(data.body);
      if (!subject.trim() && data.subject) setSubject(data.subject);
      toast.success(
        data.address_name
          ? `KI-Entwurf erstellt – mit CRM-Wissen zu ${data.address_name}`
          : 'KI-Entwurf erstellt – Empfänger nicht im CRM gefunden, daher ohne Kundenwissen',
        {
          duration: 8000,
          action: {
            label: 'Rückgängig',
            onClick: () => { setBodyHtml(vorherBody); setPristineHtml(vorherPristine); setSubject(vorherBetreff); },
          },
        },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KI-Assistent: Anfrage fehlgeschlagen');
    } finally {
      setAiBusy(false);
    }
  };

  // ─── Senden ───
  const getSignatureHtml = () => {
    if (selectedSignature === 'none') return '';
    const sig = signatures.find((s) => (s.id || s._id) === selectedSignature);
    return sig?.content_html || sig?.html || '';
  };

  // ─── Projekt-Verknüpfung: Popover öffnen + Vorschläge laden ───
  const applyLinkedProject = useCallback((proj) => {
    setLinkedProject(proj);
    try {
      if (proj) localStorage.setItem(LS_LINKED_PROJECT, JSON.stringify(proj));
      else localStorage.removeItem(LS_LINKED_PROJECT);
    } catch { /* ignore quota */ }
  }, [LS_LINKED_PROJECT]);

  // Auto-Verknüpfung: Wenn Empfänger genau zu einem Projekt gehört → Historie automatisch aktivieren.
  // Läuft sanft im Hintergrund sobald „An" ausgefüllt ist und wir noch keinen manuellen Vorschlag haben.
  useEffect(() => {
    const primary = toEmails[0];
    if (!open || !primary || linkedProject || autoLinked) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/email/project-suggest', {
          params: { to_email: primary, subject: subject || '', body: '' },
        });
        if (cancelled) return;
        const d = res.data || {};
        const emailMatches = (d.suggestions || []).filter((s) => s.source === 'email_match');
        setMatchedAddressId(d.matched_address_id || null);
        if (emailMatches.length === 1) {
          applyLinkedProject(emailMatches[0]);
          setLogInProjectHistory(true);
          setAutoLinked(true);
        }
      } catch { /* silent */ }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, toEmails[0], subject]);

  const openProjectLinkPopover = async (mode) => {
    setProjLinkMode(mode);
    if (mode === 'participant') setLinkAsParticipant(true);
    if (mode === 'history') setLogInProjectHistory(true);
    setProjLinkOpen(true);
    // Nur einmal frisch laden, wenn wir noch keine Vorschläge haben ODER Empfänger/Betreff sich geändert haben.
    if (!projSuggestLoading) {
      setProjSuggestLoading(true);
      try {
        const primaryTo = toEmails[0] || '';
        const plain = (pristineHtml || bodyHtml || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        const res = await api.get('/email/project-suggest', {
          params: { to_email: primaryTo, subject: subject || '', body: plain.substring(0, 1500) },
        });
        const d = res.data || {};
        setProjSuggestions(d.suggestions || []);
        setMatchedAddressId(d.matched_address_id || null);
      } catch {
        setProjSuggestions([]);
      } finally {
        setProjSuggestLoading(false);
      }
      // Alle Projekte für die Suchliste laden (leichtgewichtig)
      if (projSearchAll.length === 0) {
        try {
          const r = await api.get('/projects', { params: { limit: 500 } });
          const list = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
          setProjSearchAll(list.map((p) => ({ project_id: p.id, name: p.name, type: p.projekttyp || p.project_type || '', status: p.status || '' })));
        } catch {
          setProjSearchAll([]);
        }
      }
    }
  };

  const handleSend = async () => {
    // Bug #2: Doppelklick-Schutz — verhindert Endlosschleife bei mehrfachem Klick
    // wenn Sending noch läuft (Anhänge können lange dauern).
    if (sending) return;
    if (toEmails.length === 0) { toast.error('Bitte mindestens einen Empfänger angeben'); return; }
    if (!subject.trim()) { toast.error('Bitte einen Betreff angeben'); return; }
    const invalidTo = toEmails.find((e) => !isValidEmail(e));
    if (invalidTo) { toast.error(`Ungültige E-Mail-Adresse: ${invalidTo}`); return; }

    setSending(true);
    try {
      // pristineHtml hat Vorrang: bei komplexen Templates muss der Empfänger das
      // Original-Layout sehen (Tabellen + Inline-CSS), nicht die TipTap-Variante.
      const sourceHtml = pristineHtml || bodyHtml;
      const sig = getSignatureHtml();
      const fullBody = sourceHtml + (sig ? `<br><br>${sig}` : '');
      const plain = fullBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      const hasHtml = fullBody.includes('<') && fullBody.includes('>');
      const attachmentsPayload = attachments.map((a) => ({ filename: a.name, file_data: a.base64, content_type: a.type }));

      // ── Bug #1: CRM-Historie beim Versand an Ansprechpartner ──────────────
      // Match: Ist der erste Empfänger die Adresse eines Ansprechpartners → dessen ID
      //         mit an das Backend liefern, damit dort ein Contact-History-Eintrag
      //         am Ansprechpartner erzeugt wird (nicht nur an der Haupt-Adresse).
      let matchedContactPersonId = null;
      try {
        const primary = (toEmails[0] || '').toLowerCase().trim();
        if (primary && Array.isArray(contactPersons) && contactPersons.length) {
          const cp = contactPersons.find((c) => (c?.email || '').toLowerCase().trim() === primary);
          if (cp && (cp.id || cp._id)) matchedContactPersonId = cp.id || cp._id;
        }
      } catch { /* ignore */ }

      let result;
      if (selectedProvider === 'outlook' && selectedOutlookAccount) {
        result = await outlookService.sendEmail({
          to: toEmails,
          subject,
          body: hasHtml ? fullBody : plain,
          is_html: hasHtml,
          cc: ccEmails.length ? ccEmails : null,
          bcc: bccEmails.length ? bccEmails : null,
          attachments: attachmentsPayload.length ? attachmentsPayload : null,
          importance: priority === 'high' ? 'high' : priority === 'low' ? 'low' : 'normal',
          requestReadReceipt,
        }, selectedOutlookAccount);
      } else {
        result = await emailService.sendEmail({
          to_email: toEmails[0],
          recipient_name: addressName || '',
          additional_to: toEmails.slice(1),
          subject,
          message: plain,
          html_body: hasHtml ? fullBody : null,
          is_html: hasHtml,
          cc: ccEmails.length ? ccEmails.join(',') : null,
          bcc: bccEmails.length ? bccEmails.join(',') : null,
          attachments: attachmentsPayload.length ? attachmentsPayload : null,
          priority,
          request_read_receipt: requestReadReceipt,
          scheduled_at: scheduledAt,
          account_id: selectedSmtpAccount || null,
          related_address_id: addressId || null,
          related_contact_person_id: matchedContactPersonId,
          related_project_id: linkedProject?.project_id || null,
        });
      }

      if (result?.success) {
        toast.success(result.message || 'E-Mail erfolgreich gesendet');
        // Serienmail (Adressliste): je Adresse, deren E-Mail noch unter den Empfängern steht, ein Eintrag
        // in Protokoll, Historie und Vertriebs-Journal – zählt als Kontakt in der Vertriebsstatistik
        const alleEmpfaenger = new Set([...toEmails, ...ccEmails, ...bccEmails].map((e) => (e || '').trim().toLowerCase()));
        const serie = Array.isArray(serienEmpfaenger)
          ? serienEmpfaenger.filter((r) => r?.address_id && alleEmpfaenger.has((r.email || '').trim().toLowerCase()))
          : [];
        if (serie.length > 0) {
          try {
            const res = await templateService.createCorrespondenceLogMehrfach({
              empfaenger: serie,
              subject,
              content_preview: plain.substring(0, 200),
              content_html: fullBody,
              sender: fromLabel,
              provider: selectedProvider,
              template_id: usedTemplate?.id || null,
              template_name: usedTemplate?.name || null,
            });
            if (res?.erfasst) toast.success(`Serienmail bei ${res.erfasst} Adressen in der Historie erfasst`);
            if (res?.fehler?.length) toast.warning(`${res.fehler.length} Adressen konnten nicht protokolliert werden`);
          } catch {
            toast.error('Serienmail gesendet, aber nicht in der Historie der Adressen erfasst');
          }
        }
        // History-Log (best effort, ignore errors)
        // Einzelmail: Protokoll für die Adresse des Composers (ohne Adresse lehnt das Backend ab)
        if (serie.length === 0 && addressId) {
          try {
            await templateService.createCorrespondenceLog({
              address_id: addressId,
              typ: 'email',
              subject,
              recipient: toEmails.join(', '),
              cc: ccEmails.join(', ') || null,
              bcc: bccEmails.join(', ') || null,
              sender: fromLabel,
              content_preview: plain.substring(0, 200),
              content_html: fullBody,
              provider: selectedProvider,
              template_id: usedTemplate?.id || null,
              template_name: usedTemplate?.name || null,
            });
          } catch { /* silent */ }
        }
        // Projekt-Verknüpfung (Beteiligter / Historie / Anhänge) — best effort
        if (linkedProject?.project_id && (linkAsParticipant || logInProjectHistory || attachAttachments)) {
          try {
            const linkRes = await api.post('/email/link-to-project', {
              project_id: linkedProject.project_id,
              to_email: toEmails[0],
              address_id: matchedAddressId || addressId || null,
              subject,
              body_snippet: plain.substring(0, 500),
              body_html: fullBody || '',
              cc: ccEmails,
              bcc: bccEmails,
              add_participant: !!linkAsParticipant,
              log_history: !!logInProjectHistory,
              store_attachments: !!attachAttachments && attachments.length > 0,
              attachment_target: attachmentTarget,
              attachments: (attachAttachments && attachments.length > 0)
                ? attachments.map((a) => ({ filename: a.name, file_data: a.base64, content_type: a.type }))
                : [],
            });
            const d = linkRes.data || {};
            const parts = [];
            if (linkAsParticipant) {
              if (d.participant_added) parts.push('als Beteiligte:r hinzugefügt');
              else if (d.already_participant) parts.push('bereits Beteiligte:r');
            }
            if (logInProjectHistory && d.history_logged) parts.push('in Projekt-Historie erfasst');
            if (attachAttachments && (d.attachments_stored || []).length > 0) {
              const kindLabel = attachmentTarget === 'beleg' ? 'Belege' : 'Dokumente';
              parts.push(`${d.attachments_stored.length} Anhänge als ${kindLabel} abgelegt`);
            }
            if (parts.length) toast.success(`Projekt „${d.project_name}": ${parts.join(' · ')}`);
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.warning(`Projekt-Verknüpfung: ${typeof detail === 'string' ? detail : 'Fehlgeschlagen'}`);
          }
        }
        if (onSent) onSent(result);
        handleClose();
      } else if (result?.mock) {
        toast.warning('E-Mail-Service nicht konfiguriert. E-Mail wurde simuliert.');
        handleClose();
      } else {
        toast.error(result?.message || 'Fehler beim Senden');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Fehler beim Senden'));
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = () => {
    if (subject || bodyHtml || attachments.length) {
      if (!window.confirm('Entwurf verwerfen?')) return;
    }
    handleClose();
  };

  const handleClose = () => {
    setToEmails([]); setCcEmails([]); setBccEmails([]);
    setSubject(''); setBodyHtml(''); setAttachments([]);
    clearComposerDraft();
    onClose && onClose();
  };

  // ------------------------------------------------------------------
  // Entwurfs-Sicherung: Verhindert Datenverlust bei Deploy-Reload, Tab-Close
  // oder Netzwerkabbruch. Wird per DraftManager alle 5s + bei save-drafts /
  // beforeunload synchron in localStorage gesichert und beim Wieder-Öffnen
  // des Composers angeboten.
  // ------------------------------------------------------------------
  const draftKey = open ? `email-composer:${addressId || 'no-address'}:${orderId || 'no-order'}` : null;
  const draftRestoredRef = useRef(false);
  const { restore: restoreComposerDraft, clear: clearComposerDraft } = useDraftPersistence(
    draftKey,
    () => {
      // Nur speichern, wenn tatsächlich Inhalt existiert.
      const hasContent = (subject && subject.trim())
        || (bodyHtml && bodyHtml.replace(/<[^>]+>/g, '').trim())
        || (toEmails && toEmails.length)
        || (ccEmails && ccEmails.length)
        || (bccEmails && bccEmails.length);
      if (!hasContent) return null;
      return {
        subject, bodyHtml, pristineHtml,
        toEmails, ccEmails, bccEmails,
        ccVisible, bccVisible,
      };
    },
  );

  useEffect(() => {
    if (!open || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const d = restoreComposerDraft();
    if (!d) return;
    const hasContent = (d.subject || '').trim()
      || (d.bodyHtml || '').replace(/<[^>]+>/g, '').trim()
      || (d.toEmails && d.toEmails.length)
      || (d.ccEmails && d.ccEmails.length)
      || (d.bccEmails && d.bccEmails.length);
    if (!hasContent) return;
    toast('Nicht gespeicherter E-Mail-Entwurf gefunden', {
      description: 'Möchtest du deine letzte Bearbeitung wiederherstellen?',
      duration: 15000,
      action: {
        label: 'Wiederherstellen',
        onClick: () => {
          if (d.subject) setSubject(d.subject);
          if (d.bodyHtml) setBodyHtml(d.bodyHtml);
          if (d.pristineHtml) setPristineHtml(d.pristineHtml);
          if (Array.isArray(d.toEmails) && d.toEmails.length) setToEmails(d.toEmails);
          if (Array.isArray(d.ccEmails) && d.ccEmails.length) { setCcEmails(d.ccEmails); setCcVisible(true); }
          if (Array.isArray(d.bccEmails) && d.bccEmails.length) { setBccEmails(d.bccEmails); setBccVisible(true); }
          toast.success('E-Mail-Entwurf wiederhergestellt');
        },
      },
      cancel: { label: 'Verwerfen', onClick: () => clearComposerDraft() },
    });
    // restore/clear sind stabile useCallbacks — nur beim Öffnen des Composers ausführen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) draftRestoredRef.current = false;
  }, [open]);

  if (!open) return null;
  // Wenn der User auf einen anderen Tab wechselt, bleibt der Composer im DOM
  // (State-Erhaltung), wird aber nicht sichtbar gerendert. Zurückklick auf den
  // „E-Mail schreiben"-Tab reaktiviert ihn.
  if (hiddenByTab) return null;
  if (minimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[200] bg-white shadow-xl rounded-xl border border-gray-200 px-4 py-2 flex items-center gap-3 cursor-pointer hover:shadow-2xl"
        onClick={() => setMinimized(false)}
        data-testid="composer-minimized"
      >
        <Mail className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-medium text-gray-800 truncate max-w-[220px]">
          {subject || 'Neue E-Mail'}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleDiscard(); }}
          className="w-7 h-7 rounded-md hover:bg-gray-100 inline-flex items-center justify-center text-gray-500"
          aria-label="Verwerfen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      data-testid="email-composer-v2"
    >
      {/* Optionaler dezenter Backdrop nur, wenn maximiert */}
      {maximized && (
        <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={() => setMinimized(true)} />
      )}
      <div
        className="absolute bg-white shadow-2xl rounded-xl flex flex-col overflow-hidden border border-gray-200 pointer-events-auto"
        style={
          maximized
            ? { top: '2vh', left: '1vw', width: '98vw', height: '96vh' }
            : { left: geo.x, top: geo.y, width: geo.width, height: geo.height }
        }
      >
        {/* Title bar (Drag-Handle) — Sidebar-Blue */}
        <div
          className={`flex items-center justify-between gap-2 px-4 py-2.5 border-b border-blue-800/30 text-white ${
            maximized ? '' : 'cursor-move select-none'
          }`}
          style={{ background: 'linear-gradient(90deg, #0060c0, #0053ac)' }}
          onMouseDown={onTitleMouseDown}
          data-testid="composer-drag-handle"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Mail className="w-4 h-4 text-white shrink-0" />
            <span className="text-sm font-semibold text-white truncate">Neue E-Mail</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button type="button" onClick={() => setMinimized(true)} className="w-7 h-7 rounded-md hover:bg-white/15 inline-flex items-center justify-center text-white border border-white/20" title="Minimieren" data-testid="composer-minimize">
              <Minus className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setMaximized((v) => !v)} className="w-7 h-7 rounded-md hover:bg-white/15 inline-flex items-center justify-center text-white border border-white/20" title={maximized ? 'Verkleinern' : 'Maximieren'} data-testid="composer-maximize">
              <Square className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={handleDiscard} className="w-7 h-7 rounded-md hover:bg-red-500/80 inline-flex items-center justify-center text-white border border-white/20" title="Schließen" data-testid="composer-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Recipients */}
        <div className="px-4 py-1 border-b border-gray-100">
          {/* Von */}
          <div className="flex items-center gap-3 py-1.5 border-b border-gray-100">
            <span className="w-14 text-xs font-medium text-gray-500 shrink-0">Von</span>
            {senderOptions.length > 1 ? (
              <select
                value={selectedSenderKey}
                onChange={(e) => handleSenderChange(e.target.value)}
                className="flex-1 text-sm text-gray-800 bg-transparent border-0 focus:ring-0 focus:outline-none cursor-pointer truncate -ml-0.5"
                data-testid="composer-from-select"
                title="Absender-Adresse wählen"
              >
                {senderOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.name && o.name !== o.email ? `${o.name} <${o.email}>` : o.email}
                    {o.provider === 'outlook' ? ' (Outlook)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="flex-1 text-sm text-gray-800 truncate" data-testid="composer-from">{fromLabel}</span>
            )}
            <div className="flex items-center gap-1">
              {!ccVisible && (
                <button type="button" onClick={() => setCcVisible(true)} className="text-[11px] font-semibold uppercase px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="composer-toggle-cc">CC</button>
              )}
              {!bccVisible && (
                <button type="button" onClick={() => setBccVisible(true)} className="text-[11px] font-semibold uppercase px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="composer-toggle-bcc">BCC</button>
              )}
            </div>
          </div>
          {/* An */}
          <div className="flex items-start gap-3 py-1 border-b border-gray-100">
            <span className="w-14 text-xs font-medium text-gray-500 shrink-0 pt-2">An</span>
            <RecipientChips
              emails={toEmails}
              onChange={setToEmails}
              placeholder="Empfänger hinzufügen…"
              testIdPrefix="composer-to"
              onOpenAddressbook={() => { setPickerTarget('to'); setPickerModus('adressbuch'); setPickerOpen(true); }}
              onOpenDatenfilter={() => { setPickerTarget('to'); setPickerModus('datenfilter'); setPickerOpen(true); }}
            />
          </div>
          {/* #3 — Auswahl bei mehreren E-Mail-Adressen im Adressdatensatz */}
          {addressEmailOptions.length > 1 && (
            <div className="flex items-start gap-3 py-1.5 border-b border-gray-100" data-testid="composer-address-emails">
              <span className="w-14 text-[11px] font-medium text-gray-400 shrink-0 pt-1 leading-tight">Adressen<br/>dieses<br/>Kontakts</span>
              <div className="flex-1 flex flex-wrap gap-1.5 py-0.5">
                {addressEmailOptions.map((o) => {
                  const active = toEmails.includes(o.email);
                  return (
                    <button
                      key={o.email}
                      type="button"
                      onClick={() => toggleAddressEmail(o.email)}
                      className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                      title={o.email}
                      data-testid={`composer-address-email-${o.email}`}
                    >
                      {active ? <Check className="w-3 h-3" /> : <MailIcon className="w-3 h-3 opacity-60" />}
                      <span className="font-medium max-w-[160px] truncate">{o.label}</span>
                      <span className={`hidden sm:inline truncate max-w-[180px] ${active ? 'text-blue-100' : 'text-gray-400'}`}>&lt;{o.email}&gt;</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* CC */}
          {ccVisible && (
            <div className="flex items-start gap-3 py-1 border-b border-gray-100">
              <span className="w-14 text-xs font-medium text-gray-500 shrink-0 pt-2">CC</span>
              <RecipientChips
                emails={ccEmails}
                onChange={setCcEmails}
                placeholder="CC-Empfänger…"
                testIdPrefix="composer-cc"
                onOpenAddressbook={() => { setPickerTarget('cc'); setPickerModus('adressbuch'); setPickerOpen(true); }}
              />
            </div>
          )}
          {/* BCC */}
          {bccVisible && (
            <div className="flex items-start gap-3 py-1 border-b border-gray-100">
              <span className="w-14 text-xs font-medium text-gray-500 shrink-0 pt-2">BCC</span>
              <RecipientChips
                emails={bccEmails}
                onChange={setBccEmails}
                placeholder="BCC-Empfänger…"
                testIdPrefix="composer-bcc"
                onOpenAddressbook={() => { setPickerTarget('bcc'); setPickerModus('adressbuch'); setPickerOpen(true); }}
              />
            </div>
          )}
          {/* Betreff */}
          <div className="flex items-center gap-3 py-1.5">
            <button
              type="button"
              onClick={() => setTemplateModalOpen(true)}
              title="E-Mail-Vorlage auswählen"
              className="w-14 text-xs font-medium text-gray-500 shrink-0 text-left hover:text-[#1E3A5F] hover:underline cursor-pointer"
              data-testid="composer-subject-label-templates"
            >
              Betreff
            </button>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff dieser E-Mail"
              className="flex-1 py-1 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400 font-semibold text-gray-900"
              data-testid="composer-subject"
            />
          </div>
          {/* Anweisung an die KI – nur für die KI, wird nicht versendet */}
          <div className="flex items-start gap-3 py-1.5 border-t border-gray-100" data-testid="composer-ki-anweisung-row">
            <span
              className="w-14 text-xs font-medium text-fuchsia-700 shrink-0 pt-1.5 inline-flex items-center gap-1"
              title="Anweisung an die KI – wird nicht mitversendet"
            >
              <Sparkles className="w-3.5 h-3.5" /> KI
            </span>
            <textarea
              value={kiAnweisung}
              onChange={(e) => {
                setKiAnweisung(e.target.value);
                // mitwachsen bis max. ~5 Zeilen
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 112)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !aiBusy) { e.preventDefault(); runKiAnweisung(); }
              }}
              rows={1}
              maxLength={4000}
              placeholder="Anweisung an die KI, z. B. „Personalisiertes Anschreiben zum Thema …“"
              title="Nur für die KI – wird nicht versendet. Die KI nutzt das CRM-Wissen zum Empfänger (Kundenprofil, Kontakthistorie, Leads, Projekte)."
              className="flex-1 py-1 text-sm bg-transparent border-0 resize-none focus:outline-none focus:ring-0 placeholder:text-gray-400 text-gray-800 max-h-28"
              data-testid="composer-ki-anweisung"
            />
            <button
              type="button"
              onClick={runKiAnweisung}
              disabled={aiBusy || !kiAnweisung.trim()}
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 mt-0.5 text-xs font-semibold rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Entwurf mit CRM-Wissen zum Empfänger erstellen (Enter)"
              data-testid="composer-ki-anweisung-btn"
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {aiBusy ? 'Schreibt…' : 'Entwurf erstellen'}
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto" onContextMenu={handleEditorContextMenu}>
          <ComposerEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            onEditorReady={(ed) => { editorRef.current = ed; }}
            modeRef={editorModeRef}
            pristineHtml={pristineHtml}
            onPristineChange={(html) => setPristineHtml(html)}
            onUnlockPristine={() => setPristineHtml(null)}
            textBlocksRef={textBlocksRef}
            resolveSnippetRef={resolveSnippetRef}
            onBodyContextMenu={(pos) => setCtxMenu({ open: true, ...pos })}
            bodyApiRef={bodyApiRef}
          />

          {/* Anhänge-Liste */}
          {attachments.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/40 space-y-1">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Anhänge</div>
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 text-xs bg-white rounded-md px-2 py-1 border border-gray-200" data-testid={`composer-attach-${a.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium text-gray-800 truncate">{a.name}</span>
                    <span className="text-gray-400">{formatFileSize(a.size)}</span>
                  </div>
                  <button type="button" onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="text-gray-400 hover:text-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col border-t border-gray-200 bg-gray-50/70 relative">
          {/* Sanftes Info-Band: Auto-Verknüpfung mit Projekt */}
          {linkedProject && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 text-[11px] border-b ${autoLinked ? 'bg-emerald-50/60 border-emerald-100 text-emerald-800' : 'bg-blue-50/60 border-blue-100 text-blue-800'}`}
              data-testid="composer-project-linked-banner"
            >
              <FolderKanban className={`w-3.5 h-3.5 ${autoLinked ? 'text-emerald-600' : 'text-blue-600'}`} />
              <span className="truncate">
                {autoLinked ? 'Automatisch verknüpft mit ' : 'Verknüpft mit '}
                <b>{linkedProject.name}</b>
                {logInProjectHistory && ' · Historie'}
                {linkAsParticipant && ' · Beteiligte:r'}
                {attachAttachments && attachments.length > 0 && ` · ${attachments.length} Anhänge → ${attachmentTarget === 'beleg' ? 'Belege' : 'Dokumente'}`}
              </span>
              <button
                type="button"
                onClick={() => openProjectLinkPopover(linkAsParticipant ? 'participant' : 'history')}
                className="ml-auto text-[11px] font-medium underline decoration-dotted hover:opacity-70"
                data-testid="composer-project-linked-edit"
              >
                bearbeiten
              </button>
              <button
                type="button"
                onClick={() => { applyLinkedProject(null); setLinkAsParticipant(false); setLogInProjectHistory(false); setAutoLinked(false); setAttachAttachments(false); }}
                className="text-[11px] font-medium hover:opacity-70"
                data-testid="composer-project-linked-remove"
                title="Verknüpfung entfernen"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        <div className="flex items-center justify-between gap-2 px-3 py-2 relative">
          <div className="flex items-center gap-0.5">
            {/* KI */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setAiMenuOpen((v) => !v)}
                disabled={aiBusy}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md hover:bg-fuchsia-50 text-fuchsia-700 disabled:opacity-50"
                data-testid="composer-footer-ai"
                title="KI-Assistent"
              >
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span>KI</span>
              </button>
              {aiMenuOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-52 rounded-lg shadow-xl bg-white border border-gray-200 z-[60] overflow-hidden" data-testid="composer-ai-menu">
                  {[
                    { k: 'write',    label: 'Text schreiben',   desc: 'Auf Basis Betreff' },
                    { k: 'correct',  label: 'Korrigieren',      desc: 'Rechtschreibung & Grammatik' },
                    { k: 'rephrase', label: 'Umformulieren',    desc: 'Professioneller formulieren' },
                    { k: 'shorten',  label: 'Kürzen',           desc: 'Auf das Wesentliche' },
                  ].map((opt) => (
                    <button
                      key={opt.k}
                      type="button"
                      onClick={() => runAi(opt.k)}
                      className="w-full text-left px-3 py-2 hover:bg-fuchsia-50 text-sm"
                      data-testid={`composer-ai-${opt.k}`}
                    >
                      <div className="font-medium text-gray-900">{opt.label}</div>
                      <div className="text-[11px] text-gray-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Anhänge — Quelle wählen: Laufwerk / CRM / Projekt */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setAttachMenuOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-600"
                title="Anhang hinzufügen"
                data-testid="composer-footer-attach"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              {attachMenuOpen && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setAttachMenuOpen(false)} />
                  <div className="absolute bottom-11 left-0 z-[61] w-56 bg-white border border-gray-200 rounded-lg shadow-xl py-1" data-testid="attach-source-menu">
                    <button type="button" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                      data-testid="attach-source-drive">
                      <HardDrive className="w-4 h-4 text-gray-500" /> Vom Laufwerk…
                    </button>
                    <button type="button" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setAttachMenuOpen(false); setDocPicker({ open: true, mode: 'crm' }); }}
                      data-testid="attach-source-crm">
                      <Database className="w-4 h-4 text-blue-600" /> Aus CRM-Dokumenten…
                    </button>
                    <button type="button" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setAttachMenuOpen(false); setDocPicker({ open: true, mode: 'project' }); }}
                      data-testid="attach-source-project">
                      <FolderKanban className="w-4 h-4 text-emerald-600" /> Aus Projekt-Dokumenten…
                    </button>
                  </div>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleAttachmentFiles(e.target.files); e.target.value = ''; }}
            />
            {/* Vorlagen */}
            <button
              type="button"
              onClick={() => setTemplateModalOpen(true)}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-600"
              title="Vorlage auswählen"
              data-testid="composer-footer-templates"
            >
              <FileText className="w-4 h-4" />
            </button>
            {/* Als WhatsApp-Bild */}
            <button
              type="button"
              onClick={() => {
                const inhalt = pristineHtml || bodyHtml || '';
                if (!inhalt.replace(/<[^>]*>/g, '').trim() && !/<img/i.test(inhalt)) { toast.error('Bitte zuerst einen Inhalt oder eine Vorlage einfügen'); return; }
                setWhatsAppBildOffen(true);
              }}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-[#25D366]"
              title="Als WhatsApp-Bild (PNG) ausgeben"
              data-testid="composer-footer-whatsapp-bild"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
            {/* Textbausteine */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setTbMenuOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-600"
                title="Textbaustein einfügen (oder TB+Nr. tippen)"
                data-testid="composer-footer-textblocks"
              >
                <Blocks className="w-4 h-4" />
              </button>
              {tbMenuOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-72 max-h-72 overflow-y-auto rounded-lg shadow-xl bg-white border border-gray-200 z-[60]" data-testid="composer-textblock-menu">
                  <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Textbausteine</span>
                    <button
                      type="button"
                      onClick={() => window.open('/settings?tab=correspondence&sub=textBlocks', '_blank')}
                      className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                      title="Textbausteine in den Einstellungen verwalten"
                      data-testid="composer-textblock-settings-link"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {textBlocks.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-gray-400">
                      Keine Textbausteine. Anlegen unter Einstellungen → Vorlagen → Textbausteine.
                    </div>
                  ) : (
                    textBlocks.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); insertTextBlock(b); }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2"
                        data-testid={`composer-textblock-${b.tb_number}`}
                      >
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-mono font-semibold shrink-0">
                          <Hash className="w-2.5 h-2.5" />{b.tb_number}
                        </span>
                        <span className="text-sm text-gray-800 truncate">{b.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {/* Signatur */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setSignatureMenuOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-600"
                title="Signatur einfügen"
                data-testid="composer-footer-signature"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              {signatureMenuOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-56 rounded-lg shadow-xl bg-white border border-gray-200 z-[60] overflow-hidden">
                  {signatures.map((s) => {
                    const id = s.id || s._id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); insertSignatureToBody(s); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                        data-testid={`composer-signature-${id}`}
                      >
                        <div className="font-medium text-gray-900 truncate">{s.name || 'Signatur'}</div>
                        <div className="text-[11px] text-gray-500">In Text einfügen</div>
                      </button>
                    );
                  })}
                  {signatures.length === 0 && (
                    <div className="px-3 py-3 text-xs text-gray-400">Keine Signaturen hinterlegt.</div>
                  )}
                </div>
              )}
            </div>
            {/* Projekt: Als Beteiligter verknüpfen */}
            <button
              type="button"
              onClick={() => openProjectLinkPopover('participant')}
              className={`relative w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 ${linkAsParticipant && linkedProject ? 'text-emerald-600 bg-emerald-50' : 'text-gray-600'}`}
              title="Adresse als Beteiligte:r mit Projekt verbinden"
              data-testid="composer-footer-link-participant"
            >
              <FolderPlus className="w-4 h-4" />
              {linkAsParticipant && linkedProject && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500" />
              )}
            </button>
            {/* Projekt: In Historie erfassen */}
            <button
              type="button"
              onClick={() => openProjectLinkPopover('history')}
              className={`relative w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 ${logInProjectHistory && linkedProject ? 'text-blue-600 bg-blue-50' : 'text-gray-600'}`}
              title="E-Mail in Projekt-Historie erfassen"
              data-testid="composer-footer-link-history"
            >
              <BookOpen className="w-4 h-4" />
              {logInProjectHistory && linkedProject && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500" />
              )}
            </button>
            {/* Mehr */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMoreMenuOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-600"
                title="Mehr Optionen"
                data-testid="composer-footer-more"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {moreMenuOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-64 rounded-lg shadow-xl bg-white border border-gray-200 z-[60] overflow-hidden" data-testid="composer-more-menu">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-600">Mehr Optionen</span>
                    <button
                      type="button"
                      onClick={() => setMoreMenuOpen(false)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-gray-200 text-gray-500"
                      title="Schließen"
                      data-testid="composer-more-close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Wichtigkeit</div>
                    <div className="flex items-center gap-1">
                      {['low', 'normal', 'high'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPriority(p)}
                          className={`flex-1 text-xs py-1 rounded ${priority === p ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                          data-testid={`composer-priority-${p}`}
                        >
                          {p === 'low' ? 'Niedrig' : p === 'high' ? 'Hoch' : 'Normal'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requestReadReceipt}
                      onChange={(e) => setRequestReadReceipt(e.target.checked)}
                      data-testid="composer-read-receipt"
                    />
                    <span className="flex items-start gap-1"><Bell className="w-3.5 h-3.5 mt-0.5" /> Lesebestätigung anfordern</span>
                  </label>
                  <div className="px-3 py-2 border-t border-gray-100">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Versand planen</div>
                    <input
                      type="datetime-local"
                      value={scheduledAt || ''}
                      onChange={(e) => setScheduledAt(e.target.value || null)}
                      className="w-full text-xs py-1 px-2 border border-gray-300 rounded"
                      data-testid="composer-schedule"
                    />
                    {scheduledAt && (
                      <button
                        type="button"
                        onClick={() => setScheduledAt(null)}
                        className="mt-1 text-[11px] text-gray-500 hover:text-red-600"
                      >
                        Planung zurücksetzen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleDiscard}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-red-50 text-gray-500 hover:text-red-600"
              title="Entwurf verwerfen"
              data-testid="composer-discard"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || toEmails.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 shadow-sm"
              data-testid="composer-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Senden…' : 'Senden'}
            </button>
          </div>
        </div>
        </div>

        {/* Popover: Projekt-Verknüpfung (KI-Vorschlag) */}
        {projLinkOpen && (
          <>
            <div className="fixed inset-0 z-[70]" onClick={() => setProjLinkOpen(false)} />
            <div
              className="absolute bottom-16 left-2 sm:left-40 z-[75] w-[420px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
              data-testid="composer-project-link-popover"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-gradient-to-r from-blue-50 to-emerald-50">
                <FolderKanban className="w-4 h-4 text-blue-600" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800">Projekt verknüpfen</div>
                  <div className="text-[11px] text-gray-500">KI schlägt passende Projekte vor</div>
                </div>
                <button onClick={() => setProjLinkOpen(false)} className="p-1 rounded hover:bg-white/60" aria-label="Schließen">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Aktionen */}
              <div className="px-4 py-2.5 border-b border-gray-100 space-y-1.5 bg-gray-50">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={linkAsParticipant}
                    onChange={(e) => setLinkAsParticipant(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="composer-link-participant-cb"
                  />
                  <FolderPlus className="w-3.5 h-3.5 text-emerald-600" />
                  Adresse als Beteiligte:r zum Projekt hinzufügen
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={logInProjectHistory}
                    onChange={(e) => setLogInProjectHistory(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="composer-link-history-cb"
                  />
                  <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                  E-Mail in Projekt-Historie erfassen
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attachAttachments}
                    onChange={(e) => setAttachAttachments(e.target.checked)}
                    disabled={attachments.length === 0}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 disabled:opacity-40"
                    data-testid="composer-link-attach-cb"
                  />
                  <Paperclip className="w-3.5 h-3.5 text-amber-600" />
                  <span className={attachments.length === 0 ? 'text-gray-400' : ''}>
                    Anhänge im Projekt ablegen ({attachments.length})
                  </span>
                </label>
                {attachAttachments && attachments.length > 0 && (
                  <div className="pl-6 flex items-center gap-3 text-xs text-gray-600">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="composer-attachment-target"
                        checked={attachmentTarget === 'beleg'}
                        onChange={() => setAttachmentTarget('beleg')}
                        className="w-3 h-3 text-amber-600"
                        data-testid="composer-attachment-target-beleg"
                      />
                      als Beleg (Buchhaltung + OCR)
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="composer-attachment-target"
                        checked={attachmentTarget === 'document'}
                        onChange={() => setAttachmentTarget('document')}
                        className="w-3 h-3 text-amber-600"
                        data-testid="composer-attachment-target-document"
                      />
                      als Dokument (Projekt-Ordner)
                    </label>
                  </div>
                )}
              </div>

              {/* KI-Vorschläge */}
              <div className="max-h-64 overflow-y-auto">
                {projSuggestLoading && (
                  <div className="px-4 py-6 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> KI ermittelt passende Projekte …
                  </div>
                )}
                {!projSuggestLoading && projSuggestions.length === 0 && (
                  <div className="px-4 py-3 text-xs text-gray-500">Keine automatischen Treffer. Nutze die Suche unten.</div>
                )}
                {!projSuggestLoading && projSuggestions.map((s) => {
                  const selected = linkedProject?.project_id === s.project_id;
                  return (
                    <button
                      key={s.project_id}
                      type="button"
                      onClick={() => applyLinkedProject(s)}
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-50 hover:bg-blue-50/50 transition-colors ${selected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                      data-testid={`composer-project-suggest-${s.project_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.name || '(ohne Namen)'}</div>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold ${s.source === 'email_match' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
                          {s.source === 'email_match' ? 'Direkt' : 'KI'}
                        </span>
                      </div>
                      {s.reason && <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{s.reason}</div>}
                      <div className="text-[10px] text-gray-400 mt-0.5">{s.type || '—'} · {s.status || '—'}</div>
                    </button>
                  );
                })}
              </div>

              {/* Manuelle Suche */}
              <div className="border-t border-gray-100 px-3 py-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-2" />
                  <input
                    type="text"
                    value={projSearchQuery}
                    onChange={(e) => setProjSearchQuery(e.target.value)}
                    placeholder="Anderes Projekt suchen …"
                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="composer-project-search"
                  />
                </div>
                {projSearchQuery.trim() && (
                  <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-gray-100">
                    {projSearchAll
                      .filter((p) => p.name?.toLowerCase().includes(projSearchQuery.toLowerCase()))
                      .slice(0, 20)
                      .map((p) => (
                        <button
                          key={p.project_id}
                          type="button"
                          onClick={() => { applyLinkedProject(p); setProjSearchQuery(''); }}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between gap-2"
                          data-testid={`composer-project-search-${p.project_id}`}
                        >
                          <span className="truncate">{p.name}</span>
                          <span className="text-[10px] text-gray-400 shrink-0">{p.type || '—'}</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                <div className="text-[11px] text-gray-500 truncate">
                  {linkedProject ? <>Ausgewählt: <b className="text-gray-700">{linkedProject.name}</b></> : 'Kein Projekt gewählt'}
                </div>
                <div className="flex items-center gap-1.5">
                  {linkedProject && (
                    <button
                      type="button"
                      onClick={() => { applyLinkedProject(null); setLinkAsParticipant(false); setLogInProjectHistory(false); setAutoLinked(false); }}
                      className="px-2 py-1 text-[11px] text-gray-500 hover:text-red-600"
                      data-testid="composer-project-clear"
                    >
                      Entfernen
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setProjLinkOpen(false)}
                    disabled={!linkedProject || (!linkAsParticipant && !logInProjectHistory)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="composer-project-confirm"
                  >
                    <Check className="w-3.5 h-3.5" /> Übernehmen
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Resize-Handle (bottom-right) */}
        {!maximized && (
          <div
            onMouseDown={onResizeMouseDown}
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize z-30 group"
            style={{ touchAction: 'none' }}
            data-testid="composer-resize-handle"
            title="Größe ändern"
          >
            <svg viewBox="0 0 16 16" className="w-full h-full text-gray-400 group-hover:text-blue-600 transition-colors">
              <path d="M14 14 L14 6 M14 14 L6 14 M14 10 L10 14 M14 6 L6 14" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            </svg>
          </div>
        )}
      </div>

      {/* Textbaustein-Kontextmenü (Rechtsklick im Editor) – via Portal an document.body,
          damit es nicht im transform-Stacking-Context des Composer-Modals gefangen ist */}
      {ctxMenu.open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onMouseDown={() => setCtxMenu((c) => ({ ...c, open: false }))}
            onContextMenu={(e) => { e.preventDefault(); setCtxMenu((c) => ({ ...c, open: false })); }}
            data-testid="composer-textblock-ctx-backdrop"
          />
          <div
            className="fixed w-64 max-h-72 overflow-y-auto rounded-lg shadow-2xl bg-white border border-gray-200 z-[9999] py-1"
            style={{ top: ctxMenu.y, left: ctxMenu.x }}
            data-testid="composer-textblock-contextmenu"
          >
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pasteFromClipboard(); }}
              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2 text-sm text-gray-800"
              data-testid="composer-ctx-paste"
            >
              <ClipboardPaste className="w-4 h-4 text-blue-600 shrink-0" /> Aus Zwischenablage einfügen
            </button>
            <div className="px-3 py-1.5 mt-1 border-t border-gray-100 text-[11px] uppercase tracking-wider font-semibold text-gray-500">
              Textbaustein einfügen
            </div>
            {textBlocks.length === 0 && (
              <div className="px-3 py-1.5 text-xs text-gray-400">Keine Textbausteine angelegt.</div>
            )}
            {textBlocks.map((b) => (
              <button
                key={b.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertTextBlock(b); }}
                className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2"
                data-testid={`composer-ctx-textblock-${b.tb_number}`}
              >
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-mono font-semibold shrink-0">
                  <Hash className="w-2.5 h-2.5" />{b.tb_number}
                </span>
                <span className="text-sm text-gray-800 truncate">{b.name}</span>
              </button>
            ))}
          </div>
        </>,
        document.body
      )}

      <TemplatePickerModal open={templateModalOpen} onClose={() => setTemplateModalOpen(false)} onSelect={handleSelectTemplate} recentTemplates={recentTemplates} />
      <WhatsAppBildWindow
        open={whatsAppBildOffen}
        onClose={() => setWhatsAppBildOffen(false)}
        html={pristineHtml || bodyHtml || ''}
        betreff={subject}
        empfaengerName={addressName || ''}
        addressId={addressId || null}
      />
      <EmailRecipientPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        defaultTarget={pickerTarget}
        defaultModus={pickerModus}
        hatAnEmpfaenger={toEmails.length > 0}
        onAddFilter={({ to, bcc, filterName }) => {
          const vorhanden = new Set([...toEmails, ...ccEmails, ...bccEmails].map((e) => (e || '').toLowerCase()));
          const neuTo = to.filter((e) => !vorhanden.has(e.toLowerCase()));
          neuTo.forEach((e) => vorhanden.add(e.toLowerCase()));
          const neuBcc = bcc.filter((e) => !vorhanden.has(e.toLowerCase()));
          if (neuTo.length) setToEmails((prev) => [...prev, ...neuTo]);
          if (neuBcc.length) {
            setBccEmails((prev) => [...prev, ...neuBcc]);
            setBccVisible(true);
          }
          const anzahl = neuTo.length + neuBcc.length;
          toast.success(
            `${anzahl} Empfänger aus „${filterName}“ übernommen`
            + (neuTo.length ? ` – ${neuTo[0]} im „An“, ${neuBcc.length} im BCC` : ` – alle im BCC`)
          );
        }}
        onAdd={(emails, tgt) => {
          if (tgt === 'cc') {
            setCcEmails((prev) => Array.from(new Set([...prev, ...emails])));
            setCcVisible(true);
          } else if (tgt === 'bcc') {
            setBccEmails((prev) => Array.from(new Set([...prev, ...emails])));
            setBccVisible(true);
          } else {
            setToEmails((prev) => Array.from(new Set([...prev, ...emails])));
          }
          toast.success(`${emails.length} Empfänger hinzugefügt`);
        }}
      />
      <EmailDocumentPicker
        open={docPicker.open}
        mode={docPicker.mode}
        projectId={projectId}
        addressId={addressId}
        addressName={addressName}
        onClose={() => setDocPicker((p) => ({ ...p, open: false }))}
        onSelect={addDocumentsAsAttachments}
      />
    </div>
  );
}
